import { getToken, removeToken } from "@/helpers/storage"
import { clearMe } from "@/redux/actions"
import store from "@/redux/store"
import { LoginPath } from "@/router"
import { analyzeError, createRetryStrategy } from "@/utils/errorHandling"
import axios, { AxiosInstance } from "axios"

import {
  Annouchement,
  BoostPost,
  CommentPostParams,
  CreateSuperTypeParams,
  CreateUserEventPost,
  CreateUserParams,
  CustomOrgStylesParams,
  DeleteDepartmentParams,
  GetOrderDetailParams,
  GetOrgRecognitionParams,
  GetPost,
  GetUserReceivedFeedbackPost,
  Ideas,
  LeactiveMasiveParams,
  LikePostParams,
  MoodUser,
  OrganizationBaseInfo,
  PasswordlessOTP,
  ProductParams,
  PromptMessage,
  SearchUserParams,
  SendRecognitionParams,
  SettingsPodium,
  SurveyWork,
  ToggleDepartmentStatusParams,
  UpdateAccountParams,
  UpdateFiledPostState,
  UpdateOrgRecognitionRolesParams,
  UpdateRecognitionPostText,
  UpdateSuperTypeParams,
} from "@/types"
import { isOutpoints } from "@/utils"
import { validateCommunicationFormData } from "@/utils/validation"

const TOKEN: any = getToken()

// Helper function para manejar errores de manera consistente en todos los endpoints
const handleApiError = (error: any, endpoint: string, context?: string) => {
  const errorInfo = analyzeError(error)

  // Log del error con contexto
  if (import.meta.env.DEV) {
    console.group(`API Error in ${endpoint}${context ? ` (${context})` : ""}`)
    console.error("Error details:", error)
    console.error("Error analysis:", errorInfo)
    console.groupEnd()
  }

  // Para errores de red/timeout, solo log en desarrollo
  if (errorInfo.isNetworkError || errorInfo.isTimeoutError) {
    if (import.meta.env.DEV) {
      console.warn(`Network/Timeout error in ${endpoint}:`, errorInfo.message)
    }
    return null
  }

  // Para errores críticos, log en producción también
  if (errorInfo.isAuthError || (errorInfo.status && errorInfo.status >= 500)) {
    console.error(`Critical error in ${endpoint}:`, errorInfo.message)
  }

  return null
}

// Helper function para crear requests con timeout configurado según el endpoint
const createRequestWithTimeout = ( timeout?: any) => {
  const configuredTimeout = timeout || 30000 // Default 30 seconds
  return { timeout: configuredTimeout }
}

class ApiFactory {
  api: AxiosInstance | null = null

  init(authToken?: string) {
    const baseConfig = {
      baseURL: `${import.meta.env.VITE_APP_API_ENDPOINT}`,
      timeout: 30000, // 30 segundos timeout
      headers: {
        "X-Motivy-Origin": window.location.origin,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }

    console.log("🌐 API Configuration:", {
      baseURL: import.meta.env.VITE_APP_API_ENDPOINT,
      hostname: window.location.hostname,
      environment: import.meta.env.MODE,
      origin: window.location.origin,
    })

    // Usar token fresco en cada request para evitar timing tras SSO redirect.
    // Si se pasa authToken (incluso null), intentar getToken() como fallback.
    const token =
      authToken !== undefined ? authToken || getToken() : undefined

    if (token) {
      this.api = axios.create({
        ...baseConfig,
        headers: {
          ...baseConfig.headers,
          Authorization: `Bearer ${token}`,
        },
      })
    } else {
      this.api = axios.create(baseConfig)
    }

    // Add request interceptor for better error handling
    this.api.interceptors.request.use(
      (config) => {
        // Log request for debugging (only in development)
        if (import.meta.env.DEV) {
          console.log(
            `Making ${config.method?.toUpperCase()} request to:`,
            config.url
          )
        }
        return config
      },
      (error) => {
        console.error("Request interceptor error:", error)
        return Promise.reject(error)
      }
    )

    // Add response interceptor for better error handling
    this.api.interceptors.response.use(
      (response) => {
        return response
      },
      async (error) => {
        const errorInfo = analyzeError(error)

        // Handle 401 Unauthorized errors globally
        if (error.response?.status === 401) {
          // Clean up authentication state
          removeToken()
          store.dispatch(clearMe())

          // Redirect to login if not already on login page
          const currentPath = window.location.pathname
          if (
            !currentPath.includes("/login") &&
            !currentPath.includes("/logout")
          ) {
            window.location.href = LoginPath
          }
        }

        // Handle CORS errors specifically
        if (error.code === "ERR_NETWORK") {
          console.error("Network error - possible CORS issue:", error)
        }

        // Para errores recuperables, implementar reintentos automáticos
        if (errorInfo.isRecoverable && !error.config?.__retryCount) {
          const retryStrategy = createRetryStrategy(3, 1000)
          const retryCount = error.config?.__retryCount || 0

          if (retryStrategy.shouldRetry(error, retryCount)) {
            const delay = retryStrategy.getDelay(retryCount)

            await new Promise((resolve) => setTimeout(resolve, delay))

            // Marcar el intento de reintento
            error.config.__retryCount = retryCount + 1

            // Reintentar la petición
            return this.api!.request(error.config)
          }
        }

        return Promise.reject(error)
      }
    )

    return this.api
  }
}

const api = new ApiFactory()

// * * * * * * * * * * * * * * * *
// * * * ORG basic information * * *
// * * * * * * * * * * * * * * * *

export async function getOrganization() {
  try {
    let response = await api.init(TOKEN).get<OrganizationBaseInfo>("/org")
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    return handleApiError(e, "/org", "getOrganization")
  }
}

export async function getOrgSettings() {
  const token = TOKEN
  // Si no hay token, devolvemos null
  if (!token) {
    console.warn("No token available for getOrgSettings")
    return null
  }
  try {
    let response = await api
      .init(token)
      .get("/org/settings", createRequestWithTimeout("/org/settings"))
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e: any) {
    // Si el servidor responde con 401 (Unauthorized), manejamos el error de forma controlada
    if (e.response?.status === 401) {
      console.warn("Unauthorized Access: Returning null for org settings...")
      return null
    } else {
      return handleApiError(e, "/org/settings", "getOrgSettings")
    }
  }
}

export async function getOrgBranding() {
  try {
    let response = await api.init(TOKEN).get("/org/branding")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    return handleApiError(e, "/org/branding", "getOrgBranding")
  }
}

export async function getOrgAuthFlow() {
  try {
    // Para el login inicial, no necesitamos token
    let response = await api.init().get("/org/auth_flow")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    return handleApiError(e, "/org/auth_flow", "getOrgAuthFlow")
  }
}

export async function getOrgAvailableModules() {
  try {
    let response = await api.init(TOKEN).get("/org/services")
    if (response.status === 200) {
      return response.data.data
    } else {
      console.warn("getOrgAvailableModules: Status no válido:", response.status)
      return {}
    }
  } catch (e: any) {
    console.warn("/org/services error:", e)

    // Si es un error de red o CORS, retornar un objeto vacío
    if (
      e.code === "ERR_NETWORK" ||
      e.code === "NETWORK_ERROR" ||
      e.message?.includes("Network Error")
    ) {
      console.warn(
        "Error de red detectado en getOrgAvailableModules, retornando configuración por defecto"
      )
      return {}
    }

    // Para otros errores, también retornar objeto vacío para que la app pueda continuar
    return {}
  }
}

export async function customOrgStyles(data: CustomOrgStylesParams) {
  try {
    const response = await api.init(TOKEN).patch("/org/custom", data)
    if (response.status === 200 && response.data.success) {
      return {
        data: response.data.data,
        next: response.data.next,
      }
    } else {
      throw new Error("Error en la respuesta del servidor: Estilos Org")
    }
  } catch (e) {
    console.log("/org/custom")
    console.error(e)
    throw e
  }
}

// * * * * * * * * * * *
// * * * Login-OPT * * *
// * * * * * * * * * * *

export async function login(data: any) {
  try {
    let response = await api.init(TOKEN).post("/login", data)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.log("/login")
    console.log(e)
    return null
  }
}

export async function passwordlessOTPLogin(data: PasswordlessOTP) {
  try {
    let response = await api.init(TOKEN).post("/otp/login", data)
    if (response.status === 200) {
      // Verificamos el valor de 'success' en la respuesta
      if (response.data.success === false) {
        throw new Error(response.data.error || "Error de verificación")
      }
      return response.data
    } else {
      // Lanzamos un error si el estado de la respuesta no es 200
      throw new Error("Error en la respuesta de la API")
    }
  } catch (e) {
    console.log("/otp/login")
    console.log(e)
    throw e
  }
}

export async function passwordlessOTPVerifyCode(data: any) {
  try {
    let response = await api.init(TOKEN).post("/otp/verify_code", data)

    if (response.status === 200) {
      // Verificamos el valor de 'success' en la respuesta
      if (response.data.success === false) {
        throw new Error(response.data.error || "Error de verificación")
      }
      return response.data
    } else {
      // Lanzamos un error si el estado de la respuesta no es 200
      throw new Error("Error en la respuesta de la API")
    }
  } catch (e) {
    console.log("/otp/verify_code")
    console.log(e)
    throw e
  }
}

// * * * * * * * * * * * * * * * *
// * * * Validar excel * * *
// * * * * * * * * * * * * * * * *

export async function getValidateExcel(data: any) {
  try {
    let response = await api.init(TOKEN).post("/validate/excel", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.log("/validate/excel")
    console.log(e)
    return null
  }
}

// * * * * * * * * * *
// * * * SEARCH * * *
// * * * * * * * * * *

export async function getSearchUsers(
  data: SearchUserParams,
  page: number,
  perPage: number = 0
) {
  try {
    let response = await api.init(TOKEN).post("/user/search", {
      ...data,
      page: page,
      perPage: perPage,
    })
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/user/search error:", e)
    return null
  }
}

// * * * * * * * * * * * * * * * *
// * * * SUPER TYPES * * *
// * * * * * * * * * * * * * * *

export async function getSuperTypes() {
  try {
    let response = await api.init(TOKEN).get("/super_recognition_type")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log("/super_recognition_type")
    console.log(e)
    return null
  }
}

export async function createSuperType(data: CreateSuperTypeParams) {
  try {
    let response = await api.init(TOKEN).post("/super_recognition_type", data)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.log("/super_recognition_type")
    console.log(e)
    return null
  }
}

export async function updateSuperType(data: UpdateSuperTypeParams) {
  try {
    let response = await api.init(TOKEN).patch("/super_recognition_type", data)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.log("/super_recognition_type")
    console.log(e)
    return null
  }
}

export async function deletePostType(data: any) {
  try {
    let response = await api.init(TOKEN).delete(`/org/recognition_type/${data}`)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/org/recognition_type/${data}`)
    console.log(e)
    return null
  }
}

// * * * * * * * * * * * * * * * *
// * * * Recognition Type * * *
// * * * * * * * * * * * * * * * *

export async function getAllPostTypes() {
  try {
    let response = await api.init(TOKEN).get("/recognition_type/all")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log("/recognition_type/all")
    console.log(e)
    return null
  }
}

export async function addNewPostType(data: any) {
  try {
    const response = await api.init(TOKEN).post("/recognition_type", data)

    if (response.status === 200) {
      if (response.data.success === false) {
        throw new Error(response.data.error || "Error de recognition_type")
      }
      return response.data
    } else {
      throw new Error("Error en la respuesta de la API")
    }
  } catch (e) {
    console.error("Error en /recognition_type:", e)
    throw e // Relanzamos el error para que la llamada externa lo maneje
  }
}

export async function togglePostTypeStatus(data: any) {
  try {
    const response = await api
      .init(TOKEN)
      .post("/recognition_type/activate", data)

    if (response.status === 200) {
      if (response.data.success === false) {
        throw new Error(response.data.error || "Error de recognition_type")
      }
      return response.data
    } else {
      throw new Error("Error en la respuesta de la API")
    }
  } catch (e) {
    console.error("Error en /recognition_type/activate", e)
    throw e // Relanzamos el error para que la llamada externa lo maneje
  }
}

// * * * * * * * * * * * * * * * *
// * * * Send Recognition * * *
// * * * * * * * * * * * * * * * *

export async function sendRecognition(data: SendRecognitionParams) {
  try {
    let response = await api.init(TOKEN).post("/send_feedback", data)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.log("/send_feedback")
    console.log(e)
    return null
  }
}

export async function createOrgBadge(data: FormData) {
  try {
    let response = await api.init(TOKEN).post(`/org/badges`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 30000, // 30 segundos timeout
    })
    if (response.status === 201) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor: createOrgBadge")
    }
  } catch (e) {
    console.error("Error durante la solicitud a createOrgBadge:", e)
    console.log(`/org/badges`)
    throw e
  }
}

// * * * * * * * * * * * * * * * *
// * * * * * Get Newsfeed * * * * * *
// * * * * * * * * * * * * * * * *

export interface GetNewsfeedParams {
  page?: number
  /** Cursor para paginación eficiente (evita OFFSET grande). Prioridad sobre page cuando está definido. */
  cursor?: string | null
  context?: "mobile" | "desktop"
  include_comments?: boolean
}


export async function getNewsfeed(params: GetNewsfeedParams = {}) {
  const page = params.page ?? 1
  const context = params.context ?? "desktop"
  const includeComments = params.include_comments ?? true
  const cursor = params.cursor

  const searchParams = new URLSearchParams({
    page: String(page),
    context,
    include_comments: String(includeComments),
  })
  if (cursor) searchParams.set("cursor", cursor)
  const path = `/newsfeed?${searchParams.toString()}`
  const headers: Record<string, string> = {}
  if (isOutpoints()) {
    headers["X-Motivy-Client"] = "outpoints"
  }

  console.log("🌐 API call - getNewsfeed:", path, params, headers)
  try {
    const apiInstance = api.init(TOKEN)
    // 60s: el endpoint tarda ~18s + transfer de ~1.5MB; pasar timeout en config para prioridad
    const response = await apiInstance.get(path, { headers, timeout: 60000 })
    console.log("✅ getNewsfeed response:", {
      status: response.status,
      itemsLength: response.data?.data?.items?.length,
    })
    if (response.status === 200 && response.data?.success) {
      return response.data.data
    }
    return null
  } catch (e) {
    console.error("❌ API call - getNewsfeed error:", path, e)
    return null
  }
}

// * * * * * * * * * * * * * * * *
// * * * * * Get Posts * * * * * * *
// * * * * * * * * * * * * * * * *

export async function getPost(params: GetPost) {
  const path = params?.id
    ? `/post/${params.id}?include_comments=true`
    : `/post?page=${params.page}&include_comments=true`
  console.log("🌐 API call - getPost:", path, params)
  try {
    // Usar un timeout más corto para el NewsFeed
    const apiInstance = api.init(TOKEN)
    apiInstance.defaults.timeout = 15000 // 15 segundos para NewsFeed

    let response = await apiInstance.get(path)
    console.log("✅ getPost response:", {
      status: response.status,
      dataLength: response.data?.data?.length,
    })
    if (response.status === 200) {
      return response.data.data
    } else {
      console.warn("⚠️ getPost non-200 status:", response.status)
      return null
    }
  } catch (e) {
    console.error("❌ API call - getPost error:", path, e)
    return null
  }
}

// * * * * * * * * * * * * *
// * * * GET USER POSTS * * *
// * * * * * * * * * * * * *

export async function getUserReceivedFeedback(
  params: GetUserReceivedFeedbackPost
) {
  try {
    const path = `/user/posts/received?page=${params.page}&user_id=${params.user_id}`
    console.log("🌐 API call - getUserReceivedFeedback:", path, params)

    // Usar un timeout más corto para el NewsFeed
    const apiInstance = api.init(TOKEN)
    apiInstance.defaults.timeout = 15000 // 15 segundos para NewsFeed

    let response = await apiInstance.get(path)
    console.log("✅ getUserReceivedFeedback response:", {
      status: response.status,
      dataLength: response.data?.data?.length,
    })
    if (response.status === 200) {
      return response.data.data
    } else {
      console.warn(
        "⚠️ getUserReceivedFeedback non-200 status:",
        response.status
      )
      return null
    }
  } catch (e) {
    console.error(
      "❌ API call - getUserReceivedFeedback error:",
      `/user/posts/received?page=${params.page}&user_id=${params.user_id}`,
      e
    )
    return null
  }
}

// * * * * * * * * * * * *
// * * * UPDATE POST * * *
// * * * * * * * * * * * *

export async function updateRecognitionPostText(
  params: UpdateRecognitionPostText
) {
  try {
    let response = await api
      .init(TOKEN)
      .patch<UpdateRecognitionPostText>(
        `/post/message/${params.post_id}`,
        params.data
      )
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/post/message/${params.post_id}`)
    console.log(e)
    return null
  }
}
export async function getPostTypes() {
  try {
    let response = await api.init(TOKEN).get(`/recognition_type`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/recognition_type`)
    console.log(e)
    return null
  }
}

export async function updateFiledPostState(params: UpdateFiledPostState) {
  if (params.post_id == null) {
    throw new Error("post_id es requerido para updateFiledPostState")
  }
  const filed =
    params.data != null && typeof params.data.filed === "boolean"
      ? params.data.filed
      : undefined
  if (filed === undefined) {
    throw new Error("data.filed (boolean) es requerido para updateFiledPostState")
  }
  const body = { filed }
  try {
    const response = await api
      .init(TOKEN)
      .patch(`/post/filed/${params.post_id}`, body)
    if (response.status === 200) {
      return response.data?.data ?? response.data
    }
    throw new Error(
      "Error en la respuesta del servidor: updateFiledPostState"
    )
  } catch (e) {
    if (import.meta.env.DEV) {
      console.error("/post/filed error:", params.post_id, e)
    }
    throw e
  }
}

// * * * * * * * * * * * * * * *
// * * SPECIAL RECOGNITION * * *
// * * * * * * * * * * * * * * *

export async function getUserRecognitions(
  page: number,
  id: string | undefined
) {
  try {
    const data = { user_id: id, page: page }
    let response = await api
      .init(TOKEN)
      .get(`/post/special/recognitions`, { params: data })
    if (response.status === 200) {
      return {
        data: response.data.data,
        next: response.data.next,
      }
    } else {
      console.error("Error fetching user recognitions:", response.statusText)
      return null
    }
  } catch (e) {
    console.error("Error during API request to getUserRecognitions:", e)
    console.log(`/analytics/monthly_points_summary`)
    return null
  }
}

// * * * * * * * * * * * *
// * * * DEPARTMENTS * * *
// * * * * * * * * * * * *

export async function getDepartments() {
  try {
    let response = await api.init(TOKEN).get(`/departments`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/departments`)
    console.log(e)
    return null
  }
}

export async function getOrgDepartmentsActive() {
  try {
    let response = await api.init(TOKEN).get(`/org/departments`)
    if (response.status === 200) {
      return response.data.data.filter((d: any) => d.active)
    } else {
      throw new Error("Error en la respuesta del servidor: getOrgDepartments")
    }
  } catch (e) {
    console.error("Error during API request to getOrgDepartments:", e)
    console.log(`/org/departments`)
    throw e
  }
}

export async function getOrgDepartments() {
  try {
    let response = await api.init(TOKEN).get(`/org/departments`)
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor: getOrgDepartments")
    }
  } catch (e) {
    console.error("Error during API request to getOrgDepartments:", e)
    console.log(`/org/departments`)
    throw e
  }
}

export async function deleteDepartment(data: DeleteDepartmentParams) {
  try {
    let response = await api
      .init(TOKEN)
      .delete(`/org/department/${data.department_id}`)
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor: deleteDepartment")
    }
  } catch (e) {
    console.error("Error during API request to deleteDepartment:", e)
    console.log(`/org/department/${data.department_id}`)
    throw e
  }
}

export async function updateDepartmentStatus(
  data: ToggleDepartmentStatusParams
) {
  try {
    let response = await api.init(TOKEN).post(`/org/department/activate`, data)
    if (response.status === 200) {
      return response.data
    } else {
      throw new Error(
        "Error en la respuesta del servidor: updateDepartmentStatu"
      )
    }
  } catch (e) {
    console.error("Error during API request to updateDepartmentStatu:", e)
    console.log(`/org/department/activate`)
    throw e
  }
}

export async function getGroups() {
  try {
    let response = await api.init(TOKEN).get(`/org/groups`)
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor: getGroups")
    }
  } catch (e) {
    console.error("Error during API request to getOrgDepartments:", e)
    console.log(`/org/groups`)
    throw e
  }
}

export async function getAdminGroups() {
  try {
    let response = await api.init(TOKEN).get(`/org/admin/groups`)
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor: getGroups")
    }
  } catch (e) {
    console.error("Error during API request to getOrgDepartments:", e)
    console.log(`/org/admin/groups`)
    throw e
  }
}

// * * * * * * * * * * * * *
// * * * GET COUNTRIES * * *
// * * * * * * * * * * * * *

export async function getOrgCountries() {
  try {
    let response = await api.init(TOKEN).get(`/departments`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/departments`)
    console.log(e)
    return null
  }
}

// * * * * * * * * * * * * *
// * * * GET REFERRED * * *
// * * * * * * * * * * * * *

export async function sendReferences(data: any) {
  try {
    let response = await api
      .init(TOKEN)
      .post(`/referred`, { referral_id: data })
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/referred`)
    console.log(e)
    return null
  }
}

export async function isReferred() {
  try {
    let response = await api.init(TOKEN).get(`/referred`)
    if (response.status === 200) {
      return response.data
    } else {
      return false
    }
  } catch (e: any) {
    // Para errores 400, probablemente el usuario no está referido, retornar false
    if (e.response?.status === 400) {
      return false
    }
    return false
  }
}

export async function sendReferred(id: string) {
  try {
    let response = await api
      .init(TOKEN)
      .patch(`/referred`, { referred_by_id: id })
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`referred`)
    console.log(e)
    return null
  }
}

// * * * * * * * * * * * *
// * * * POST LIKE * * * *
// * * * * * * * * * * * *

export async function likePost(data: LikePostParams) {
  try {
    let response = await api.init(TOKEN).post(`/like`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/like`)
    console.log(e)
    return null
  }
}

export async function getLikes() {
  try {
    let response = await api.init(TOKEN).get(`/likes`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/likes`)
    console.log(e)
    return null
  }
}

// * * * * * * * * * * * *
// * * * USERS ADMIN* * * *
// * * * * * * * * * * * *

export async function deleteUsersBulk(data: FormData) {
  try {
    let response = await api.init(TOKEN).post(`/user/admin/delete/bulk`, data, {
      headers: { "Content-Type": undefined },
    })
    if (response.status === 200 || response.status === 202) {
      return response.data
    }
    throw new Error("Error en la respuesta del servidor")
  } catch (e) {
    console.log(`/user/admin/delete/bulk`)
    console.log(e)
    throw e
  }
}

export async function updateBossesBulk(data: FormData) {
  try {
    let response = await api
      .init(TOKEN)
      .post(`/org/admin/update/bosses/bulk`, data, {
        headers: { "Content-Type": undefined },
      })
    if (response.status === 200 || response.status === 202 || response.status === 207) {
      return response.data
    }
    throw new Error("Error en la respuesta del servidor")
  } catch (e) {
    console.log(`/org/admin/update/bosses/bulk`)
    console.log(e)
    throw e
  }
}

export async function updateRolesBulk(data: FormData) {
  try {
    let response = await api
      .init(TOKEN)
      .post(`/org/admin/update/roles/bulk`, data, {
        headers: { "Content-Type": undefined },
      })
    if (response.status === 200 || response.status === 202 || response.status === 207) {
      return response.data
    }
    throw new Error("Error en la respuesta del servidor")
  } catch (e) {
    console.log(`/org/admin/update/roles/bulk`)
    console.log(e)
    throw e
  }
}

export async function deactivateMasiveConfirm(data: LeactiveMasiveParams) {
  try {
    let response = await api
      .init(TOKEN)
      .patch(`/org/upload/users/deactivate`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/org/upload/users/deactivate`)
    console.log(e)
    return null
  }
}

/* export async function uploadUsers(data: any) {
    try {
        let response = await api.init(TOKEN).post(`/org/upload/users`, data);
        if (response.status === 202) {
            return response.data;
        } else {
            throw new Error("Error en la respuesta del servidor");
        }
    } catch (e) {
        console.log(`/org/upload/users`);
        console.log(e);
        throw e;
    }
} */

export async function uploadUsers(data: any) {
  try {
    let response = await api.init(TOKEN).post(`/org/upload/users`, data, {
      headers: {
        "Content-Type": undefined, // 💥 esto deja que axios la detecte desde `FormData`
      },
    })

    // Aceptar 200 y 202 como respuestas válidas del backend
    if (response.status === 200 || response.status === 202) {
      return response.data
    }

    throw new Error("Error en la respuesta del servidor")
  } catch (e) {
    console.log(`/org/upload/users`)
    console.log(e)
    throw e
  }
}

export async function createUser(params: CreateUserParams) {
  try {
    // Desestructurar los datos
    const {
      userData: { birthday, admission, jobPosition, userType, points, groups },
    } = params
    // Formatear los datos antes de enviarlos
    const requestData = {
      ...params.userData,
      job_position: jobPosition,
      birthday: birthday ? birthday.format("YYYY-MM-DD") : null,
      admission_date: admission ? admission.format("YYYY-MM-DD") : null,
      user_type: userType,
      points: Boolean(points || false),
      groups: groups ? groups.map((group: any) => group) : [],
    }
    let response = await api.init(TOKEN).post(`/org/create/user`, requestData)

    if (response.status === 201) {
      return response.data
    } else {
      throw new Error("Error en la respuesta del servidor: createUser")
    }
  } catch (e) {
    console.error("Error durante la solicitud a createUser:", e)
    console.log(`/org/create/user`)
    throw e
  }
}

export async function registerUserExternal(data: any) {
  try {
    let response = await api.init(TOKEN).post(`/org/create/user/external`, data)
    if (response.status === 201) {
      return response.data.data
    } else {
      throw new Error(
        "Error en la respuesta del servidor: registerUserExternal"
      )
    }
  } catch (e) {
    console.error("Error durante la solicitud a registerUserExternal:", e)
    console.log(`/org/create/user/external`)
    throw e
  }
}

export async function updateAccount(data: UpdateAccountParams) {
  try {
    let response = await api.init(TOKEN).patch(`/user/account/update`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor: updateUser")
    }
  } catch (e) {
    console.error("Error durante la solicitud a updateUser:", e)
    console.log(`/user/update`)
    throw e
  }
}

// * * * * * * * * * * * * * * * * * * * *
// * * * PROFILE UPDATE REQUIREMENTS * * *
// * * * * * * * * * * * * * * * * * * * *

export interface ProfileUpdateRequirementsData {
  is_active: boolean
  fields: string[]
  reminder_after: number | null
  data_validity_days: number | null
  required_update: boolean
  profile_data_updated_at: string | null
}

export async function getProfileUpdateRequirements(): Promise<ProfileUpdateRequirementsData> {
  try {
    const response = await api.init(TOKEN).get(`/user/profile-update-requirements`)
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor: getProfileUpdateRequirements")
    }
  } catch (e) {
    console.error("Error durante la solicitud a getProfileUpdateRequirements:", e)
    throw e
  }
}

export interface ProfileUpdatePayload {
  city?: string
  phone?: string
  department_id?: number
  name?: string
  lastname?: string
  title?: string
  address?: string
  state?: string
  location?: string
  about_me?: string
  linkedin?: string
  twitter?: string
  date_of_birth?: string
  language?: string
  country_id?: number
  remind_later?: boolean
}

export async function patchProfileUpdate(data: ProfileUpdatePayload) {
  try {
    const response = await api.init(TOKEN).patch(`/user/profile-update`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor: patchProfileUpdate")
    }
  } catch (e) {
    console.error("Error durante la solicitud a patchProfileUpdate:", e)
    throw e
  }
}

// * * * * * * * * * * * *
// * * * USER PROFILE * * *
// * * * * * * * * * * * *

export async function getUsersBirth() {
  try {
    const response = await api.init(TOKEN).get("/users/birth")
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor al obtener productos")
    }
  } catch (e) {
    console.log("/users/birth")
    console.error(e)
    throw e
  }
}

export async function getUsersAdmission() {
  try {
    const response = await api.init(TOKEN).get("/users/admission")
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor al obtener productos")
    }
  } catch (e) {
    console.log("/users/admission")
    console.error(e)
    throw e
  }
}

export async function getUser() {
  const token = TOKEN

  // Si no hay token, devolvemos null o lanzamos un error controlado
  if (!token) {
    console.warn("No token available for getUser")
    return null
  }
  try {
    console.log("🌐 getUser API call starting...")
    let response = await api.init(token).get(`/user`)
    console.log("✅ getUser response:", {
      status: response.status,
      hasData: !!response.data?.data,
    })

    // Si el status es 200, retornamos los datos del usuario
    if (response.status === 200) {
      return response.data.data
    } else {
      // Cualquier otra respuesta se considera un error
      throw new Error("Error en la respuesta del servidor: getUser")
    }
  } catch (e: any) {
    // Si el servidor responde con 401 (Unauthorized), manejamos el error de forma controlada
    if (e.response?.status === 401) {
      console.warn("Unauthorized Access: Returning null for getUser...")
      return null // Puedes devolver null o realizar una redirección al login aquí
    } else if (e.response?.status === 403) {
      console.warn("Forbidden Access: Returning null for getUser...")
      return null
    } else if (e.code === "NETWORK_ERROR" || e.code === "ERR_NETWORK") {
      console.warn("Network error: Returning null for getUser...")
      return null
    } else {
      // Otros errores se reportan
      console.error("Error durante la solicitud a getUser:", e)
      console.log(`/user`)
      throw e
    }
  }
}

export async function setNotificationSettings(data: any) {
  try {
    let response = await api
      .init(TOKEN)
      .post(`/user/notification_settings`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error(
        "Error en la respuesta del servidor: setNotificationSettings"
      )
    }
  } catch (e) {
    console.error("Error durante la solicitud a setNotificationSettings:", e)
    console.log(`/user/notification_settings`)
    throw e
  }
}

export async function setNotificationSettingsWhatsapp(data: any) {
  try {
    let response = await api
      .init(TOKEN)
      .post(`/user/notification_settings_wpp`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error(
        "Error en la respuesta del servidor: setNotificationSettingsWhatsapp"
      )
    }
  } catch (e) {
    console.error(
      "Error durante la solicitud a setNotificationSettingsWhatsapp:",
      e
    )
    console.log(`/user/notification_settings_wpp`)
    throw e
  }
}

export async function getNotificationsCount() {
  const token = TOKEN
  // Si no hay token, devolvemos valores por defecto
  if (!token) {
    console.warn("No token available for getNotificationsCount")
    return { notification_count: 0, pending_surveys: 0 }
  }
  try {
    let response = await api.init(token).get(`/user/notification_count`)
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error(
        "Error en la respuesta del servidor: getNotificationsCount"
      )
    }
  } catch (e: any) {
    // Si el servidor responde con 401 (Unauthorized), manejamos el error de forma controlada
    if (e.response?.status === 401) {
      console.warn(
        "Unauthorized Access: Returning default notification count..."
      )
      return { notification_count: 0, pending_surveys: 0 }
    } else {
      // Otros errores se reportan
      console.error("Error durante la solicitud a getNotificationsCount:", e)
      console.log(`/user/notification_count`)
      throw e
    }
  }
}

export async function getNotifications(page: any) {
  try {
    const data = { page: page }
    let response = await api.init(TOKEN).get(
      `/user/notification`,
      { params: data } // El uso de 'params' es adecuado para query params
    )

    if (response.status === 200) {
      return {
        data: response.data.data,
        next: response.data.next,
      }
    } else {
      throw new Error("Error en la respuesta del servidor: getNotifications")
    }
  } catch (e) {
    console.error("Error durante la solicitud a getNotifications:", e)
    console.log(`/user/notification`)
    throw e
  }
}

// * * * * * * * * * * * * *
// * * * USER-PUBLIC * * * *
// * * * * * * * * * * * * *

export async function getPublicProfile(id: any) {
  try {
    let response = await api.init(TOKEN).get(`/user/${id}/public`)
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor: getPublicProfile")
    }
  } catch (e) {
    console.error("Error durante la solicitud a getPublicProfile:", e)
    console.log(`/user/${id}/public`)
    throw e
  }
}

export async function getUserSentFeedback(id: any, page: any) {
  try {
    const data = { user_id: id, page: page }
    let response = await api
      .init(TOKEN)
      .get(`/user/posts/sent`, { params: data })
    if (response.status === 200) {
      return {
        data: response.data.data, // Datos devueltos
        next: response.data.next, // Información sobre la próxima página
      }
    } else {
      throw new Error("Error en la respuesta del servidor: getUserSentFeedback")
    }
  } catch (e) {
    console.error("Error durante la solicitud a getUserSentFeedback:", e)
    console.log(`/user/posts/sent`)
    throw e
  }
}

export async function getUsersReceivedFeedback(id: any, page: any) {
  try {
    const data = { page: page, user_id: id }
    let response = await api
      .init(TOKEN)
      .get(`/user/posts/received`, { params: data })
    if (response.status === 200) {
      return {
        data: response.data.data, // Datos devueltos
        next: response.data.next, // Información sobre la próxima página
      }
    } else {
      throw new Error(
        "Error en la respuesta del servidor: getUsersReceivedFeedback"
      )
    }
  } catch (e) {
    console.error("Error durante la solicitud a getUsersReceivedFeedback:", e)
    console.log(`/user/posts/received`)
    throw e
  }
}

// * * * * * * * * * *
// * * * EVENTS * * * *
// * * * * * * * * * *


export async function uploadEventAvatar(eventId: string | number, file: File) {
  const formData = new FormData()
  formData.append("file", file)

  const response = await api.init(TOKEN).post(`/events/${eventId}/avatar`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    transformRequest: (data) => data,
  })

  if ((response.status === 201 || response.status === 200) && response.data?.success) {
    // response.data.data.avatar_url
    return response.data.data
  }

  throw new Error(response.data?.message || "Upload avatar failed")
}

export async function getEventAvatar(eventId: string | number) {
  const response = await api.init(TOKEN).get(`/events/${eventId}/avatar`)

  if (response.status === 200 && response.data?.success) {
    // { avatar_url: string | null }
    return response.data.data
  }

  throw new Error(response.data?.message || "Failed to fetch avatar")
}

export async function deleteEventAvatar(eventId: string | number) {
  const response = await api.init(TOKEN).delete(`/events/${eventId}/avatar`)

  if (response.status === 200 && response.data?.success) {
    return true
  }

  throw new Error(response.data?.message || "Delete avatar failed")
}


export async function getEvents() {
  try {
    const response = await api.init(TOKEN).get("/events")
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor al obtener productos")
    }
  } catch (e) {
    console.log("/events")
    console.error(e)
    throw e
  }
}

export async function createUserEventPost(data: CreateUserEventPost) {
  try {
    const response = await api.init(TOKEN).post("/user/event/post", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor al crear eventos")
    }
  } catch (e) {
    console.log("/user/event/post")
    console.error(e)
    throw e
  }
}

export async function createOrgEvent(data: any) {
  try {
    const response = await api.init(TOKEN).post("/events/create", data)
    if (response.status === 200 && response.data.success) {
      return response.data
    } else {
      throw new Error("Error en la respuesta del servidor al crear eventos")
    }
  } catch (e) {
    console.log("/events/create")
    console.error(e)
    throw e
  }
}

export async function uploadEventImage(eventId: string | number, file: File) {
  const formData = new FormData()
  formData.append("file", file)

  const response = await api.init(TOKEN).post(
    `/events/${eventId}/image`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      transformRequest: (data) => data, 
    }
  )

  if ((response.status === 201 || response.status === 200) && response.data?.success) {
    return response.data
  }
  throw new Error(response.data?.message || "Upload failed")
}

export async function uploadEventImages(
  eventId: string | number,
  variant: "desktop" | "mobile",
  files: File[]
) {
  const formData = new FormData()

  files.forEach((file) => {
    formData.append("files", file) // backend acepta files[]
  })

  const response = await api
    .init(TOKEN)
    .post(`/events/${eventId}/images?variant=${variant}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      transformRequest: (data) => data,
    })

  if ((response.status === 201 || response.status === 200) && response.data?.success) {
    return response.data.data // array de imágenes creadas
  }

  throw new Error(response.data?.message || "Upload failed")
}

export async function getEventImages(eventId: string | number) {
  const response = await api.init(TOKEN).get(`/events/${eventId}/images`)

  if (response.status === 200 && response.data?.success) {
    return response.data.data
    // {
    //   desktop: Image[],
    //   mobile: Image[]
    // }
  }

  throw new Error("Failed to fetch event images")
}

export async function deleteEventImage(imageId: number) {
  const response = await api.init(TOKEN).delete(`/events/images/${imageId}`)

  if (response.status === 200 && response.data?.success) {
    return true
  }

  throw new Error("Delete failed")
}

export async function updateOrgEvent(data: any) {
  try {
    const response = await api
      .init(TOKEN)
      .patch(`/events/edit/${data.eventId}`, data)
    if (response.status === 200 && response.data.success) {
      return response.data.data
    } else {
      throw new Error(
        "Error en la respuesta del servidor al actualizar eventos"
      )
    }
  } catch (e) {
    console.log(`/events/edit/${data.eventId}`)
    console.error(e)
    throw e
  }
}

export async function getUserEventPosts() {
  try {
    const response = await api.init(TOKEN).get("/user/events")
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor al obtener eventos")
    }
  } catch (e) {
    console.log("/user/events")
    console.error(e)
    throw e
  }
}

export async function deleteUserEvent(data: string) {
  try {
    const response = await api.init(TOKEN).delete(`/events/${data}`)
    if (response.status === 200 && response.data.success) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor al eliminar evento")
    }
  } catch (e) {
    console.log(`/events/${data}`)
    console.error(e)
    throw e
  }
}

export async function getEventParticipants(event_id: number) {
  try {
    let response = await api.init(TOKEN).get(`/event/${event_id}/participants`)
    if (response.status === 200 && response.data.success) {
      return response.data.participants
    } else {
      throw new Error(
        "Error en la respuesta del servidor al actualizar eventos"
      )
    }
  } catch (e) {
    console.log(`/event/${event_id}/participants`)
    console.error(e)
    throw e
  }
}

export async function changePassword(data: any) {
  try {
    const response = await api.init(TOKEN).post("/user/challenge/pass", data)
    if (response.status === 200 && response.data.success) {
      return response.data.data
    } else {
      throw new Error(
        "Error en la respuesta del servidor al recuperar contraseña"
      )
    }
  } catch (e) {
    console.log("/user/challenge/pass")
    console.error(e)
    throw e
  }
}

export async function passwordRecovery(data: any) {
  try {
    const response = await api.init(TOKEN).post("/user/recovery/pass", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error(
        "Error en la respuesta del servidor al confirmar nueva contraseña"
      )
    }
  } catch (e) {
    console.log("/user/recovery/pass")
    console.error(e)
    throw e
  }
}

export async function confirmPasswordRecovery(data: any) {
  try {
    const response = await api
      .init(TOKEN)
      .post("/user/confirm/pass/recovery", data)

    console.log("Respuesta del servidor:", response)
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error(
        "Error en la respuesta del servidor al cambiar contraseña"
      )
    }
  } catch (e) {
    console.log("/user/confirm/pass/recovery")
    console.error(e)
    throw e
  }
}

// * * * * * * * * * * * *
// * * * CommentPost * * * *
// * * * * * * * * * * * *

export async function commentPost(data: CommentPostParams) {
  try {
    let response = await api.init(TOKEN).post(`/comment`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/comment`)
    console.log(e)
    return null
  }
}

export async function updateComment(data: any) {
  try {
    let response = await api.init(TOKEN).post(`/comment/edit`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/comment/edit`)
    console.log(e)
    return null
  }
}

export async function deleteComment(data: any) {
  try {
    let response = await api.init(TOKEN).delete(`/comment/${data}`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/comment/${data}`)
    console.log(e)
    return null
  }
}

// ******* SubCommnets ********

export async function subCommentPost(data: any) {
  try {
    let response = await api.init(TOKEN).post(`/subcomment`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor al crear subcomment")
    }
  } catch (e) {
    console.log(`/subcomment`)
    console.log(e)
    return null
  }
}

export async function updateSubComment(data: any) {
  try {
    let response = await api.init(TOKEN).patch(`/subcomment/edit`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/subcomment/edit`)
    console.log(e)
    throw e
  }
}

export async function deleteSubComment(data: any) {
  try {
    let response = await api.init(TOKEN).delete(`/subcomment/${data}`)
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error(
        "Error en la respuesta del servidor al eliminar subcomment"
      )
    }
  } catch (e) {
    console.log(`/subcomment/${data}`)
    console.log(e)
    throw e
  }
}

// * * * * * * * * * * * *
// * * * BoostPost * * * *
// * * * * * * * * * * * *

export async function boostPost(data: BoostPost) {
  try {
    let response = await api.init(TOKEN).post(`/boost`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/boost`)
    console.log(e)
    return null
  }
}

// * * * * * * * * * * * *
// * * * Analytics * * * *
// * * * * * * * * * * * *

export async function getPostsBytype() {
  try {
    let response = await api.init(TOKEN).get(`/analytics/recognition_types`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/analytics/recognition_types`)
    console.log(e)
    return null
  }
}

export async function getRecognitionsBytype() {
  try {
    let response = await api.init(TOKEN).get(`/analytics/recognition_types_v2`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/analytics/recognition_types_v2`)
    console.log(e)
    return null
  }
}

export async function getUserSessions() {
  try {
    let response = await api.init(TOKEN).get(`/analytics/user/session`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/analytics/user/session`)
    console.log(e)
    return null
  }
}

export async function getAllPointsMonthly() {
  try {
    let response = await api
      .init(TOKEN)
      .get(`/analytics/points_by_channel_by_month`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/analytics/points_by_channel_by_month`)
    console.log(e)
    return null
  }
}

export async function getCustomAnalytics() {
  try {
    let response = await api.init(TOKEN).get(`/custom_analytics`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/custom_analytics`)
    console.log(e)
    return null
  }
}

export async function getAnalyticsOverview() {
  try {
    let response = await api.init(TOKEN).get(`/analytics/overview`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/analytics/overview`)
    console.log(e)
    return null
  }
}

export async function getPointsByChannel() {
  try {
    let response = await api.init(TOKEN).get(`/analytics/points_by_channel`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/analytics/points_by_channel`)
    console.log(e)
    return null
  }
}

export async function getTotalPoints() {
  try {
    let response = await api.init(TOKEN).get(`/analytics/total_points`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/analytics/total_points`)
    console.log(e)
    return null
  }
}

export async function getSocialPointsStats() {
  try {
    let response = await api
      .init(TOKEN)
      .get(`/analytics/social_points_recognitions`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/analytics/social_points_recognitions`)
    console.log(e)
    return null
  }
}

export async function getPostsBytypePublic(user_id: any) {
  try {
    let response = await api
      .init(TOKEN)
      .get(`/analytics/${user_id}/recognition_types`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/analytics/${user_id}/recognition_types`)
    console.log(e)
    return null
  }
}

export async function getPublicCustomAnalytics() {
  try {
    let response = await api.init(TOKEN).get(`/custom_analytics/public`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/custom_analytics/public`)
    console.log(e)
    return null
  }
}

export async function getPointsByMonth(year: any, month: any) {
  try {
    const data = { year, month }
    let response = await api
      .init(TOKEN)
      .get(`/analytics/monthly_points_summary`, { params: data })
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/analytics/monthly_points_summary`)
    console.log(e)
    return null
  }
}

export async function chartSummaryV2(
  department: any,
  category: any,
  valueFrom: any,
  valueTo: any,
  sendUsers: any,
  country: any,
  type: any
) {
  try {
    const data = {
      department,
      category,
      valueFrom,
      valueTo,
      sendUsers,
      country,
      type,
    }
    let response = await api
      .init(TOKEN)
      .get(`/chart/summary/v3`, { params: data })
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/chart/summary/v3`)
    console.log(e)
    return null
  }
}

// * * * * * * * * * * * *
// * * * COMMUNICATIONS * *
// * * * * * * * * * * * *

export async function sendCommAll(data: FormData) {
  try {
    // Validar el FormData usando la función de utilidad
    const validation = validateCommunicationFormData(data)
    if (!validation.isValid) {
      throw new Error(`Datos inválidos: ${validation.errors.join(", ")}`)
    }

    let response = await api
      .init(TOKEN)
      .post<Annouchement>(`/communication/all`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 30000, // 30 segundos timeout
      })

    if (response.status === 200) {
      return response.data
    } else {
      throw new Error(
        `Error en la respuesta del servidor: ${response.status} ${response.statusText}`
      )
    }
  } catch (e: any) {
    console.error("Error en sendCommAll:", e)

    // Si es un error de red, proporcionar mensaje más específico
    if (e.code === "ERR_NETWORK" || e.code === "NETWORK_ERROR") {
      throw new Error("Error de conexión. Verifica tu conexión a internet.")
    }

    // Si es un error de timeout
    if (e.code === "ECONNABORTED") {
      throw new Error("La solicitud tardó demasiado. Intenta nuevamente.")
    }

    // Si es un error del servidor (4xx, 5xx)
    if (e.response) {
      const status = e.response.status
      const errorMessage =
        e.response.data?.error ||
        e.response.data?.message ||
        `Error del servidor: ${status}`
      throw new Error(errorMessage)
    }

    // Para otros errores, usar el mensaje original
    throw new Error(e.message || "Error desconocido al enviar el comunicado")
  }
}

export async function sendCommGroup(data: FormData) {
  try {
    // Validar el FormData usando la función de utilidad
    const validation = validateCommunicationFormData(data)
    if (!validation.isValid) {
      throw new Error(`Datos inválidos: ${validation.errors.join(", ")}`)
    }

    // Validar ID del grupo
    const id = data.get("id")
    if (!id) {
      throw new Error("ID del grupo es requerido")
    }

    let response = await api
      .init(TOKEN)
      .post<Annouchement>(`/communication/group/${id}`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 30000, // 30 segundos timeout
      })

    if (response.status === 200) {
      return response.data
    } else {
      throw new Error(
        `Error en la respuesta del servidor: ${response.status} ${response.statusText}`
      )
    }
  } catch (e: any) {
    console.error("Error en sendCommGroup:", e)

    // Si es un error de red, proporcionar mensaje más específico
    if (e.code === "ERR_NETWORK" || e.code === "NETWORK_ERROR") {
      throw new Error("Error de conexión. Verifica tu conexión a internet.")
    }

    // Si es un error de timeout
    if (e.code === "ECONNABORTED") {
      throw new Error("La solicitud tardó demasiado. Intenta nuevamente.")
    }

    // Si es un error del servidor (4xx, 5xx)
    if (e.response) {
      const status = e.response.status
      const errorMessage =
        e.response.data?.error ||
        e.response.data?.message ||
        `Error del servidor: ${status}`
      throw new Error(errorMessage)
    }

    // Para otros errores, usar el mensaje original
    throw new Error(
      e.message || "Error desconocido al enviar el comunicado al grupo"
    )
  }
}

export async function getComms(page: any) {
  try {
    const data = { page: page }
    const response = await api.init(TOKEN).get(`/communications`, {
      params: data,
      timeout: 60000, // 60s para evitar que cuelgue indefinidamente
    })

    if (response.status === 200) {
      return response.data
    } else {
      throw new Error("Error en la respuesta del servidor: getComms")
    }
  } catch (e: any) {
    console.error("Error durante la solicitud a getComms:", e)
    if (e?.code === "ECONNABORTED" || e?.message?.includes("timeout")) {
      console.warn(
        "Timeout en /communications. El servidor tarda demasiado o la respuesta es muy grande."
      )
    }
    throw e
  }
}

export async function sendCommDepartment(data: any) {
  try {
    let response = await api.init(TOKEN).post(`/communication/department`, data)
    if (response.status === 200) {
      return response.data
    } else {
      throw new Error("Error en la respuesta del servidor: sendCommDepartment")
    }
  } catch (e) {
    console.error("Error durante la solicitud a sendCommDepartment:", e)
    console.log(`/communication/department`)
    throw e
  }
}

export async function getUserSurveys() {
  try {
    let response = await api.init(TOKEN).get(`/surveys`)
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor: getUserSurveys")
    }
  } catch (e) {
    console.error("Error durante la solicitud a getUserSurveys:", e)
    console.log(`/surveys`)
    throw e
  }
}

export async function getGroupComms(group: string, page: number) {
  try {
    const response = await api
      .init(TOKEN)
      .get(`/communications/group/${group}`, { params: { page } })

    if (response.status === 200) {
      return response.data
    } else {
      throw new Error("Error en la respuesta del servidor: getGroupComms")
    }
  } catch (e) {
    console.error("Error durante la solicitud a getGroupComms:", e)
    console.log(`/communications/group/${group}`)
    throw e
  }
}

export async function getGroupCommsOnly() {
  try {
    const response = await api.init(TOKEN).get(`/communication/groups`)

    if (response.status === 200) {
      return response.data
    } else {
      throw new Error("Error en la respuesta del servidor: getGroupCommsOnly")
    }
  } catch (e) {
    console.error("Error durante la solicitud a getGroupComms:", e)
    console.log(`/communication/groups`)
    throw e
  }
}

export async function getDepartmentComms(department: any, page: number) {
  try {
    const response = await api
      .init(TOKEN)
      .get(`/communications/department/${department}`, { params: { page } })

    if (response.status === 200) {
      return response.data
    } else {
      throw new Error("Error en la respuesta del servidor: getDepartmentComms")
    }
  } catch (e) {
    console.error("Error durante la solicitud a getDepartmentComms:", e)
    console.log(`/communications/department/${department}`)
    throw e
  }
}

export async function getAdminComms(page: number) {
  try {
    // Verificar que el token esté disponible
    if (!TOKEN) {
      throw new Error("Token de autenticación no disponible")
    }

    // Usar timeout más largo para conexiones lentas
    const response = await api.init(TOKEN).get(`/admin/communications`, {
      params: { page },
      timeout: 60000, // 60 segundos timeout para conexiones lentas
    })

    if (response.status === 200) {
      return response.data
    } else {
      throw new Error(
        `Error en la respuesta del servidor: getAdminComms - Status: ${response.status}`
      )
    }
  } catch (e) {
    // Usar el nuevo sistema de manejo de errores
    return handleApiError(
      e,
      `/admin/communications?page=${page}`,
      "getAdminComms"
    )
  }
}

export async function getCommGroup(id: number) {
  try {
    if (id) {
      const response = await api.init(TOKEN).get(`/communication/group/${id}`)

      if (response.status === 200) {
        return response.data.data
      } else {
        throw new Error("Error en la respuesta del servidor: getCommGroup")
      }
    }
  } catch (e) {
    console.error("Error durante la solicitud a getCommGroup:", e)
    console.log(`/communication/group/${id}`)
    throw e
  }
}

export async function getRecoGroupSolo(id: number) {
  try {
    if (id) {
      const response = await api.init(TOKEN).post(`/recognition/group/${id}`)

      if (response.status === 200) {
        return response.data.data
      } else {
        throw new Error("Error en la respuesta del servidor: getRecoGroupSolo")
      }
    } else {
      throw new Error("Error en la respuesta del servidor: getRecoGroupSolo")
    }
  } catch (e) {
    console.error("Error durante la solicitud a getRecoGroupSolo:", e)
    console.log(`/recognition/group/${id}`)
    throw e
  }
}

export async function getCommGroups() {
  try {
    const response = await api.init(TOKEN).get(`/communication/groups`)

    if (response.status === 200) {
      return response.data.data // Devolver solo el array de grupos
    } else {
      throw new Error("Error en la respuesta del servidor: getCommGroups")
    }
  } catch (e) {
    console.error("Error durante la solicitud a getCommGroups:", e)
    console.log(`/communication/groups`)
    throw e
  }
}

// * * * * * * * * * * * * * * *
// * * * GET ANNOUNCEMENTS * * *
// * * * * * * * * * * * * * * *
export async function getAnnouncements(page: number = 1) {
  try {
    console.log(
      "🌐 API call - getAnnouncements:",
      `/communications?page=${page}`
    )
    const response = await api.init(TOKEN).get(`/communications`, {
      params: { page },
      timeout: 60000, // 60 segundos para announcements
    })

    console.log("✅ getAnnouncements response:", {
      status: response.status,
      dataLength: response.data?.data?.length,
      notFiledDataLength: response.data?.notFiledData?.length,
    })

    if (response.status === 200) {
      return response.data
    } else {
      console.warn("⚠️ getAnnouncements non-200 status:", response.status)
      throw new Error("Error en la respuesta del servidor: getAnnouncements")
    }
  } catch (e: any) {
    console.error("❌ Error durante la solicitud a getAnnouncements:", e)
    console.log(`/communications?page=${page}`)

    // Si es timeout, log más específico
    if (e.code === "ECONNABORTED" || e.message?.includes("timeout")) {
      console.warn(
        `⚠️ Timeout en getAnnouncements página ${page}. Considera optimizar la consulta del servidor.`
      )
    }

    throw e
  }
}

// * * * * * * * * * * * * * *
// Points Request/Transfer *
// * * * * * * * * * * * * * *

export async function addPointRequest(data: any) {
  try {
    const response = await api.init(TOKEN).post("/point_request", data)
    if (response.status === 200 && response.data.success) {
      return response.data
    } else {
      throw new Error("Error en la solicitud de puntos")
    }
  } catch (e) {
    console.error(e)
    throw e
  }
}

export async function getPointRequests(pending: any) {
  try {
    const response = await api
      .init(TOKEN)
      .get("/point_request", { params: { pending } })
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error("Error en la solicitud de puntos")
    }
  } catch (e) {
    console.error("Error en getPointRequests:", e)
    throw e
  }
}

// * * * * * * * * * * * *
// * * * Catalog-Cart * * *
// * * * * * * * * * * * *

// Cart
export const getAllProducts = async (
  category: any,
  justCorporative: any = false
) => {
  try {
    console.log("🛍️ API call - getAllProducts:", { category, justCorporative })

    // Construir la ruta según la categoría y el flag `justCorporative`
    let path = "/product"
    path +=
      category !== -1
        ? `/category/${category}?just_corporative=${justCorporative}`
        : ""

    const response = await api.init(TOKEN).post(path)

    if (response.status === 200) {
      console.log("✅ Productos obtenidos exitosamente:", {
        totalProducts: response.data.data?.length || 0,
        hasNext: !!response.data.next,
        category,
        justCorporative,
      })

      // Retornar la data y la paginación
      return {
        data: response.data.data || [], // Lista de productos
        next: response.data.next, // Paginación
        total: response.data.total || 0, // Total de productos
        success: true,
      }
    } else {
      console.warn("⚠️ getAllProducts non-200 status:", response.status)
      throw new Error(
        "Error en la respuesta del servidor al obtener productos cart."
      )
    }
  } catch (e) {
    console.error("❌ Error en el servicio de productos:", e)
    throw e
  }
}

export async function getProducts(data: ProductParams, _page: number) {
  try {
    console.log("🛍️ API call - getProducts:", { data, page: _page })

    const response = await api.init(TOKEN).post("/product", data)

    if (response.status === 200 && response.data.success) {
      console.log("✅ getProducts success:", {
        totalProducts: response.data.data?.length || 0,
        hasNext: response.data.hasNext || false,
        page: _page,
      })

      return {
        data: response.data.data || [],
        hasNext: response.data.hasNext || false,
        nextCursor: response.data.nextCursor || null,
        total: response.data.total || 0,
        success: true,
      }
    } else {
      console.warn("⚠️ getProducts non-200 status:", response.status)
      throw new Error("Error en la respuesta del servidor al obtener productos")
    }
  } catch (e) {
    console.error("❌ Error en getProducts:", e)
    throw e
  }
}

export async function getCart() {
  try {
    let response = await api.init(TOKEN).get(`/cart`)
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor: getCart")
    }
  } catch (e) {
    console.error("Error durante la solicitud a getCart:", e)
    console.log(`/cart`)
    throw e
  }
}

export async function addToCart(items: any) {
  try {
    const data = { items }
    let response = await api.init(TOKEN).post(`/cart`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor: addToCart")
    }
  } catch (e) {
    console.error("Error durante la solicitud a addToCart:", e)
    console.log(`/cart`)
    throw e
  }
}

export async function addToCartExternal(items: any) {
  try {
    const data = { items }
    let response = await api.init(TOKEN).post(`/cart/external`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor: addToCartExternal")
    }
  } catch (e) {
    console.error("Error durante la solicitud a addToCartExternal:", e)
    console.log(`/cart/external`)
    throw e
  }
}

export async function deleteFromCart(items: any) {
  try {
    const response = await api.init(TOKEN).delete(`/cart`, {
      data: { items },
    })
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor: deleteFromCart")
    }
  } catch (e) {
    console.error("Error durante la solicitud a deleteFromCart:", e)
    console.log(`/cart`)
    throw e
  }
}

export async function updateCart(data: any) {
  try {
    let response = await api.init(TOKEN).post(`/update_cart`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor: updateCart")
    }
  } catch (e) {
    console.error("Error durante la solicitud a updateCart:", e)
    console.log(`/update_cart`)
    throw e
  }
}

export async function buyProducts(data: any) {
  console.log("data api: ", data)
  try {
    let response = await api.init(TOKEN).post(`/buy_products`, data)
    if (response.status === 200) {
      return response.data
    } else {
      throw new Error("Error en la respuesta del servidor: buyProducts")
    }
  } catch (e) {
    console.error("Error durante la solicitud a buyProducts:", e)
    console.log(`/buy_products`)
    throw e
  }
}

export async function getProductCategories() {
  try {
    console.log("🏷️ API call - getProductCategories")

    let response = await api.init(TOKEN).get(`/product_category`)

    if (response.status === 200) {
      console.log("✅ getProductCategories success:", {
        totalCategories: response.data.data?.length || 0,
      })

      return {
        data: response.data.data || [],
        success: true,
      }
    } else {
      console.warn("⚠️ getProductCategories non-200 status:", response.status)
      throw new Error(
        "Error en la respuesta del servidor: getProductCategories"
      )
    }
  } catch (e) {
    console.error("❌ Error durante la solicitud a getProductCategories:", e)
    throw e
  }
}

export async function getProductFormAnswers(id: number | null) {
  try {
    if (!!id && id) {
      const response = await api.init(TOKEN).get(`/orders/answers/${id}`)

      if (response.status === 200) {
        return response.data.data
      } else {
        throw new Error(
          "Error en la respuesta del servidor: getProductFormAnswers"
        )
      }
    }
  } catch (e) {
    console.error("Error durante la solicitud a getProductFormAnswers:", e)
    console.log(`/orders/answers/${id}`)
    throw e
  }
}

// * * * * * * * * * * * * * *
// * * * SETTINGS-PODIUM * * *
// * * * * * * * * * * * * * *

export async function settingsPodium(data: SettingsPodium) {
  try {
    const response = await api.init(TOKEN).post("/org/settings/podium", data)
    if (response.status === 200 && response.data.success) {
      return response.data.data
    } else {
      throw new Error(
        "Error en la respuesta del servidor: configuraciones podium"
      )
    }
  } catch (e) {
    console.log("/org/settings/podium")
    console.error(e)
    throw e
  }
}

// * * * * * * * * * * * * *
// * * * photo-profile * * *
// * * * * * * * * * * * * *

//  Función de proxy para imágenes de perfil - desactivada por usar imageURL directa
/*
export async function getUserProfilePhotos(user_id: string | number) {
  try {
    // Validar que el user_id sea válido
    if (!user_id || user_id === "undefined" || user_id === "null") {
      throw new Error("ID de usuario inválido")
    }

    // Configuramos la petición para que reciba una respuesta de tipo Blob
    const response = await api
      .init(TOKEN)
      .get(`/user/profile-photo/downgrade/${user_id}`, {
        responseType: "blob", // Asegura que recibes un Blob en lugar de un string
        timeout: 10000, // 10 segundos de timeout
      })

    if (response.status === 200 && response.data) {
      // Verificar que el Blob no esté vacío
      if (response.data.size === 0) {
        throw new Error("Imagen vacía recibida del servidor")
      }
      return response.data
    } else {
      throw new Error(
        "Error en la respuesta del servidor: getUserProfilePhotos"
      )
    }
  } catch (e: any) {
    // Usar el sistema centralizado de manejo de errores
    handleSilentError(e, `getUserProfilePhotos-${user_id}`)

    // Re-lanzar el error para que sea manejado por el hook
    throw e
  }
}
*/

export async function updateProfilePhoto(data: FormData) {
  try {
    // Configuramos la petición para que reciba una respuesta de tipo Blob
    const response = await api.init(TOKEN).put(`/user/profile-photo`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 30000, // 30 segundos timeout
    })
    if (response.status === 200) {
      return response.data
    } else {
      throw new Error("Error en la respuesta del servidor: updateProfilePhoto")
    }
  } catch (e) {
    console.error("Error durante la solicitud a updateProfilePhoto:", e)
    console.log(`/user/profile-photo`)
    throw e
  }
}

export async function updateUserBirthdayPhoto(data: FormData) {
  try {
    // Configuramos la petición para que reciba una respuesta de tipo Blob
    const response = await api.init(TOKEN).patch(`/user/birthday-image`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 30000, // 30 segundos timeout
    })
    if (response.status === 200) {
      return response.data
    } else {
      throw new Error(
        "Error en la respuesta del servidor: updateUserBirthdayPhoto"
      )
    }
  } catch (e) {
    console.error("Error durante la solicitud a updateUserBirthdayPhoto:", e)
    console.log(`/user/birthday-image`)
    throw e
  }
}

export async function deleteUserBirthdayPhoto(data: number) {
  try {
    const response = await api
      .init(TOKEN)
      .delete(`/user/birthday-image/${data}`)

    if (response.status === 200) {
      return response.data
    } else {
      throw new Error("Error en la respuesta del servidor: deleteProfileImage")
    }
  } catch (e) {
    console.error("Error durante la solicitud a deleteProfileImage:", e)
    throw e
  }
}

export async function deleteProfileImage() {
  try {
    const response = await api.init(TOKEN).delete(`/user/profile_image`)

    if (response.status === 200) {
      return response.data
    } else {
      throw new Error("Error en la respuesta del servidor: deleteProfileImage")
    }
  } catch (e) {
    console.error("Error durante la solicitud a deleteProfileImage:", e)
    throw e
  }
}

/// *** WORKPLACE *** ///

/// MOODS

export async function getMoods() {
  try {
    let response = await api.init(TOKEN).get(`/moods`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/moods`)
    console.log(e)
    return null
  }
}

export async function getMoodsUser(start_date: string, end_date: string) {
  try {
    let response = await api.init(TOKEN).get(`/users/moods/history`, {
      params: {
        start_date,
        end_date,
      },
    })
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/users/moods/history`)
    console.log(e)
    return null
  }
}

export async function postMoodUser(mood_id: MoodUser) {
  try {
    const response = await api.init(TOKEN).post("/users/moods", mood_id)
    if (response.status === 200 && response.data.success) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor: user moods")
    }
  } catch (e) {
    console.log("/users/moods")
    console.error(e)
    throw e
  }
}

/// SURVEYS-WORKPLACE

export async function postSurveyWork(surveyData: SurveyWork) {
  try {
    const response = await api.init(TOKEN).post("/surveys/create", surveyData)
    if (response.status === 200 && response.data.success) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor: workplace survey")
    }
  } catch (e) {
    console.log("/surveys")
    console.error(e)
    throw e
  }
}

export async function getWorkSurveys() {
  try {
    let response = await api.init(TOKEN).get(`/allsurveys`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/allsurveys`)
    console.log(e)
    return null
  }
}

export async function postSurveyWorkUser(data: any) {
  try {
    const response = await api.init(TOKEN).post("/surveys/responses", data)
    if (response.status === 200 && response.data.success) {
      return response.data.data
    } else {
      throw new Error(
        "Error en la respuesta del servidor: workplace survey responses"
      )
    }
  } catch (e) {
    console.log("/surveys/responses")
    console.error(e)
    throw e
  }
}

export async function getSurveysResponseUser(surveyId: number) {
  try {
    let response = await api.init(TOKEN).get(`/surveys/${surveyId}/responses`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/surveys/${surveyId}/responses`)
    console.log(e)
    return null
  }
}

export async function getAllSurveysRespWork() {
  try {
    let response = await api.init(TOKEN).get(`/surveys/allresponses`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/surveys/allresponses`)
    console.log(e)
    return null
  }
}

export async function sendIdeas(data: FormData) {
  try {
    let response = await api.init(TOKEN).post<Ideas>(`/ideas`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 30000, // 30 segundos timeout
    })
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`ideas`)
    console.log(e)
    return null
  }
}

//// *** FITNESS *** ////

export async function createChallenges(data: any) {
  try {
    const response = await api.init(TOKEN).post("/org/challenge/create", data)
    if (response.status === 200 && response.data.success) {
      return response.data.data
    } else {
      throw new Error(
        "Error en la respuesta del servidor: creacion de reto fitness"
      )
    }
  } catch (e) {
    console.log("/org/challenge/create")
    console.error(e)
    throw e
  }
}

export async function getListCategory() {
  try {
    let response = await api.init(TOKEN).get(`/org/challenge_category/list`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/org/challenge_category/list`)
    console.log(e)
    return null
  }
}

export async function getListChallenges() {
  try {
    let response = await api.init(TOKEN).get(`/org/fitness/challenges`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/org/fitness/challenges`)
    console.log(e)
    return null
  }
}

export async function updateChallStatus(data: any) {
  try {
    let response = await api
      .init(TOKEN)
      .patch("/org/challenge/active_status/toggle", data)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.log("/org/challenge/active_status/toggle")
    console.log(e)
    return null
  }
}

export async function getListGoals() {
  try {
    let response = await api.init(TOKEN).get(`/org/challenge_goal/list`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/org/challenge_goal/list`)
    console.log(e)
    return null
  }
}

export async function getTypeChall() {
  try {
    let response = await api.init(TOKEN).get(`/org/challenge_type/list`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/org/challenge_type/list`)
    console.log(e)
    return null
  }
}

export async function createCategChallenge(data: any) {
  try {
    const response = await api
      .init(TOKEN)
      .post("/org/challenge_category/create", data)
    if (response.status === 200 && response.data.success) {
      return response.data.data
    } else {
      throw new Error(
        "Error en la respuesta del servidor: /org/challenge_category/create"
      )
    }
  } catch (e) {
    console.log("/org/challenge_category/create")
    console.error(e)
    throw e
  }
}

export async function updateChallenge(data: any) {
  try {
    let response = await api.init(TOKEN).patch("/org/challenge/update", data)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.log("/org/challenge/update")
    console.log(e)
    return null
  }
}

export async function updateChallArchived(data: any) {
  try {
    let response = await api
      .init(TOKEN)
      .patch("/org/challenge/archived/toggle", data)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.log("/org/challenge/archived/toggle")
    console.log(e)
    return null
  }
}

export async function getReactions() {
  try {
    let response = await api.init(TOKEN).get(`/reactions`)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/reactions`)
    console.log(e)
    return null
  }
}

//// COVERAGES ////

export async function createCover(data: any) {
  try {
    const response = await api.init(TOKEN).post("/org/create/coverage", data)
    if (response.status === 201 && response.data.success) {
      return response
    } else {
      throw new Error("Error en la respuesta del servidor al crear coberturas")
    }
  } catch (e) {
    console.log("/org/create/coverage")
    console.error(e)
    throw e
  }
}

export async function postAsignCover(data: any) {
  try {
    const response = await api
      .init(TOKEN)
      .post("/org/assigned/coverage/user", data)
    if (response.status === 200 && response.data.success) {
      return response.data
    } else {
      throw new Error("Error en la respuesta del servidor al asignar cobertura")
    }
  } catch (e) {
    console.log("/org/assigned/coverage/user")
    console.error(e)
    throw e
  }
}

export async function getCovers() {
  try {
    let response = await api.init(TOKEN).get(`/org/all/coverage`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/org/all/coverage`)
    console.log(e)
    return null
  }
}

export async function updateCover(data: any, id: any) {
  try {
    let response = await api
      .init(TOKEN)
      .patch(`/org/update/coverage/${id}`, data)
    if (response.status === 200) {
      return response
    } else {
      return null
    }
  } catch (e) {
    console.log(`/org/update/coverage/${id}`)
    console.log(e)
    return null
  }
}

// SURVEY

export async function updateSurveyRecognition(data: any, id: any) {
  try {
    let response = await api
      .init(TOKEN)
      .patch(`/org/survey/${id}/recognition`, data)
    if (response.status === 200) {
      return response
    } else {
      return null
    }
  } catch (e) {
    console.log(`/org/survey/${id}/recognition`)
    console.log(e)
    return null
  }
}

export async function getEverHealth() {
  try {
    let response = await api.init(TOKEN).get(`/get-patient-otp`)
    if (response.status === 200) {
      return response.data
    } else {
      throw new Error("Error en la respuesta del servidor: getEverHealth")
    }
  } catch (e) {
    console.error("Error durante la solicitud a getEverHealth:", e)
    console.log(`/get-patient-otp`)
    throw e
  }
}

//PROGRAMS

export async function newSubmitProgram(data: any) {
  try {
    const response = await api.init(TOKEN).post("/program/course/submit", data)
    if (response.status === 200 && response.data.success) {
      return response.data.data
    } else {
      throw new Error(
        "Error en la respuesta del servidor: /program/course/submit"
      )
    }
  } catch (e) {
    console.log("/program/course/submit")
    console.error(e)
    throw e
  }
}

export async function getPrograms(page: any) {
  try {
    let response = await api.init(TOKEN).get("/program", page)
    if (response.status === 200) {
      return response.data
    } else {
      throw new Error("Error en la respuesta del servidor: program")
    }
  } catch (e) {
    console.error('Error durante la solicitud a "/program":', e)
    console.log("/program")
    throw e
  }
}

export async function newProgram(data: any) {
  try {
    const response = await api.init(TOKEN).post("/program", data)
    if (response.status === 200 && response.data.success) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor: /program")
    }
  } catch (e) {
    console.log("/program")
    console.error(e)
    throw e
  }
}

export async function getProgramSubmissions(programId: any) {
  try {
    let response = await api.init(TOKEN).get("/program/submit/" + programId)
    if (response.status === 200) {
      return response.data
    } else {
      throw new Error("Error en la respuesta del servidor: /program/submit")
    }
  } catch (e) {
    console.error('Error durante la solicitud a "/program/submit":', e)
    console.log("/program/submit")
    throw e
  }
}

export async function editProgram(data: any) {
  try {
    const response = await api.init(TOKEN).post("/program/edit", data)
    if (response.status === 200 && response.data.success) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor: /program/edit")
    }
  } catch (e) {
    console.log("/program/edit")
    console.error(e)
    throw e
  }
}

export async function deletedProgram(data: any) {
  try {
    const response = await api.init(TOKEN).post("/program/deleted", data)
    if (response.status === 200 && response.data.success) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor: /program/deleted")
    }
  } catch (e) {
    console.log("/program/deleted")
    console.error(e)
    throw e
  }
}

export async function submitProgram(data: any) {
  try {
    const response = await api
      .init(TOKEN)
      .post(`/program/${data.program_type}/submit`, data)
    if (response.status === 200 && response.data.success) {
      return response.data.data
    } else {
      throw new Error(
        "Error en la respuesta del servidor: /program/${data.program_type}/submit"
      )
    }
  } catch (e) {
    console.log("/program/${data.program_type}/submit")
    console.error(e)
    throw e
  }
}

export async function assignCustomProgramWinners(data: any) {
  try {
    const response = await api.init(TOKEN).post("/program/custom/assign", data)
    if (response.status === 200 && response.data.success) {
      return response.data.data
    } else {
      throw new Error(
        "Error en la respuesta del servidor: /program/custom/assign"
      )
    }
  } catch (e) {
    console.log("/program/custom/assign")
    console.error(e)
    throw e
  }
}

export async function assignCourseProgramWinners(data: any) {
  try {
    const response = await api.init(TOKEN).post("/program/course/assign", data)
    if (response.status === 200 && response.data.success) {
      return response.data.data
    } else {
      throw new Error(
        "Error en la respuesta del servidor: /program/course/assign"
      )
    }
  } catch (e) {
    console.error(e)
    throw e
  }
}

export async function assignGenericProgramWinners(data: any) {
  try {
    const response = await api.init(TOKEN).post("/program/generic/assign", data)
    if (response.status === 200 && response.data.success) {
      return response.data.data
    } else {
      throw new Error(
        "Error en la respuesta del servidor: /program/generic/assign"
      )
    }
  } catch (e) {
    console.error(e)
    throw e
  }
}

//IMC

export async function getAllImcUsers(page = 1, perPage = 10) {
  try {
    const response = await api
      .init(TOKEN)
      .get(`/user/body_metrics/all_with_users`, {
        params: { page, per_page: perPage },
      })

    if (response.status === 200) {
      return {
        data: response.data.data,
        pagination: response.data.pagination,
      }
    } else {
      throw new Error(
        "Error en la respuesta del servidor: /user/body_metrics/all_with_users"
      )
    }
  } catch (e) {
    console.error(
      "Error durante la solicitud a /user/body_metrics/all_with_users:",
      e
    )
    throw e
  }
}

// Delete User

export async function postUserRequestDelete() {
  try {
    const response = await api.init(TOKEN).post("/user/request-delete")
    if (response.status === 200 && response.data.success) {
      return response.data
    } else {
      throw new Error(
        "Error en la respuesta del servidor al /user/request-delete"
      )
    }
  } catch (e) {
    console.log("/user/request-delete")
    console.error(e)
    throw e
  }
}

// * * * * * * * * * * * * * * * *
// * * * ORG RECOGNITIONS (BADGES) * * *
// * * * * * * * * * * * * * * * *

export async function getOrgRecognitions(params: GetOrgRecognitionParams) {
  try {
    let response = await api
      .init(TOKEN)
      .get(`/org/recognition?page=${params.page}`)
    if (response.status === 200) {
      return { data: response.data.data, next: response.data.next }
    } else {
      return null
    }
  } catch (e) {
    console.log(`/org/recognition?page=${params.page}`)
    console.log(e)
    return null
  }
}

export async function getOrg() {
  try {
    let response = await api.init(TOKEN).get("/org")
    if (response.status === 200) {
      return { data: response.data.data, next: response.data.next }
    } else {
      return null
    }
  } catch (e) {
    console.error(e)
    return null
  }
}

export async function updateOrgRecognitionRoles(
  params: UpdateOrgRecognitionRolesParams
) {
  try {
    let response = await api
      .init(TOKEN)
      .patch("/recognition_type/roles", params)
    if (response.status === 200) {
      return "done"
    } else {
      return null
    }
  } catch (e) {
    console.log("/recognition_type/roles")
    console.log(e)
    return null
  }
}

export async function editOrgRecognition(id: any, data: any) {
  try {
    let response = await api.init(TOKEN).patch(`/org/recognition/${id}`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/org/recognition/${id}`)
    console.log(e)
    return null
  }
}

export async function deleteSubtype(id: number) {
  try {
    let response = await api.init(TOKEN).delete(`/sub_recognition_type/${id}`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/sub_recognition_type/${id}`)
    console.log(e)
    return null
  }
}

export async function updateSubtype(params: {
  subtypeID: number
  subtypeName: string
}) {
  try {
    let response = await api.init(TOKEN).patch(`/sub_recognition_type`, params)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.log("/sub_recognition_type")
    console.log(e)
    return null
  }
}

// ========================================
// ========== FUNCIONES MIGRADAS DE API V3 ================
// ========================================

// ========================================
// ========== USER ORDERS ================
// ========================================

export async function getUserOrders(page: any) {
  try {
    // Asegurar que page sea un número válido
    const pageNumber = typeof page === "number" ? page : parseInt(page) || 1
    console.log("Fetching orders for page:", pageNumber)

    // Usar el token para autenticación
    let response = await api.init(TOKEN).get(`/orders?page=${pageNumber}`)

    if (response.status === 200) {
      return response.data.data
    } else {
      console.error("Orders API returned non-200 status:", response.status)
      return null
    }
  } catch (e) {
    console.error("/orders error:", e)
    return null
  }
}

export async function getCity() {
  try {
    let response = await api.init(TOKEN).get("/city")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/city error:", e)
    return null
  }
}

// ========================================
// ========== PUBLICITY ===================
// ========================================

export async function getPublicity() {
  try {
    let response = await api.init(TOKEN).get("/publicity/user")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/publicity/user error:", e)
    return null
  }
}

// ========================================
// ========== PROGRAM =====================
// ========================================

export async function getProgramCategories() {
  try {
    let response = await api.init(TOKEN).get("/program_category")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/program_category error:", e)
    return null
  }
}

// ========================================
// ========== SUGGESTIONS =================
// ========================================

export async function getSuggestion(data: FormData) {
  try {
    let response = await api.init(TOKEN).post("/suggestion", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/suggestion error:", e)
    return null
  }
}

export async function downloadFile(data: any) {
  try {
    let response = await api.init(TOKEN).post("/suggestion/file", data, {
      responseType: "blob",
    })
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/suggestion/file error:", e)
    return null
  }
}

export async function importUsersFileCSV(data: any) {
  try {
    let response = await api.init(TOKEN).post("/org/import/users", data, {
      responseType: "blob",
    })
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/import/users error:", e)
    return null
  }
}

export async function unsubscribeUsersFileCSV(data: any) {
  try {
    let response = await api.init(TOKEN).post("/org/unsubscribe/users", data, {
      responseType: "blob",
    })
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/unsubscribe/users error:", e)
    return null
  }
}

// ========================================
// ========== COMPLAINTS ==================
// ========================================

export async function getComplaint(data: FormData) {
  try {
    let response = await api.init(TOKEN).post("/complaint", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/complaint error:", e)
    return null
  }
}

export async function downloadFileComplaint(data: any) {
  try {
    let response = await api.init(TOKEN).post("/complaint/file", data, {
      responseType: "blob",
    })
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/complaint/file error:", e)
    return null
  }
}

// ========================================
// ========== SURVEYS =====================
// ========================================

export async function createSurvey(data: any) {
  try {
    let response = await api.init(TOKEN).post("/org/survey", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/survey error:", e)
    return null
  }
}

export async function editSurveyUrl(data: any) {
  try {
    let response = await api
      .init(TOKEN)
      .patch(`/admin/surveys/${data?.id}`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/admin/surveys error:", e)
    return null
  }
}

export async function submitSurvey(id: number, data: any) {
  try {
    let response = await api.init(TOKEN).post(`/survey/${id}/submit`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/survey/${id}/submit error:`, e)
    return null
  }
}

export async function submitEvaluation(id: number, data: any) {
  try {
    let response = await api.init(TOKEN).post(`/evaluation/${id}/submit`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/evaluation/${id}/submit error:`, e)
    return null
  }
}

export async function activateSurvey(id: number) {
  try {
    let response = await api.init(TOKEN).patch(`/org/survey/${id}/activate`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/org/survey/${id}/activate error:`, e)
    return null
  }
}

export async function activateProgram(id: number) {
  try {
    let response = await api.init(TOKEN).patch(`/org/program/${id}/activate`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/org/program/${id}/activate error:`, e)
    return null
  }
}

export async function multiResponseActivateSurvey(id: number) {
  try {
    let response = await api
      .init(TOKEN)
      .patch(`/org/survey/${id}/multi-response`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/org/survey/${id}/multi-response error:`, e)
    return null
  }
}

export async function activateDateSurvey(id: number, data: any) {
  try {
    let response = await api.init(TOKEN).patch(`/org/survey/${id}/date`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/org/survey/${id}/date error:`, e)
    return null
  }
}

export async function setSurveyPoints(id: number, points: any) {
  try {
    let response = await api
      .init(TOKEN)
      .patch(`/org/survey/${id}/points`, { points })
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/org/survey/${id}/points error:`, e)
    return null
  }
}

export async function notifySurvey(id: number, data: any) {
  try {
    let response = await api.init(TOKEN).post(`/org/survey/${id}/notify`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/org/survey/${id}/notify error:`, e)
    return null
  }
}

// ========================================
// ========== AUTHENTICATION ==============
// ========================================

export async function getAzureToken(data: any) {
  try {
    let response = await api.init(TOKEN).post("/login/saml2", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/login/saml2 error:", e)
    return null
  }
}

// ========================================
// ========== USER MANAGEMENT =============
// ========================================

export async function getUserProfilePhoto(data: any) {
  try {
    let response = await api
      .init(TOKEN)
      .post("/user/profile-photo/downgrade", data, {
        responseType: "blob",
      })
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/user/profile-photo/downgrade error:", e)
    return null
  }
}

export async function deleteUser(data: any) {
  try {
    let response = await api.init(TOKEN).post("/user/admin/delete", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/user/admin/delete error:", e)
    return null
  }
}

export async function editUserAsAdmin(data: any) {
  try {
    let response = await api.init(TOKEN).post("/user/update", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      throw new Error("Error en la respuesta del servidor: editUserAsAdmin")
    }
  } catch (e) {
    console.error("/user/update error:", e)
    throw e
  }
}

export async function getAllUsers() {
  try {
    let response = await api.init(TOKEN).get("/all_users")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/all_users error:", e)
    return null
  }
}

export async function getRemoveDataUser() {
  try {
    let response = await api.init(TOKEN).get("/user/privacy/remove")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/user/privacy/remove error:", e)
    return null
  }
}

export async function changeUserActiveState(data: any) {
  try {
    let response = await api.init(TOKEN).post("/user/active/state", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/user/active/state error:", e)
    return null
  }
}

// ========================================
// ========== ANALYTICS & PODIUM ==========
// ========================================

export async function getRankRecognitionsSend(user_id?: number) {
  try {
    let path = `/analytics/send/recongnitions/rank`
    if (user_id) {
      path = path.concat(`?user_id=${user_id}`)
    }
    let response = await api.init(TOKEN).get(path)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/analytics/send/recongnitions/rank error:", e)
    return null
  }
}

export async function getTransactionHistory(data: any) {
  try {
    let response = await api
      .init(TOKEN)
      .post("/analytics/transaction_history", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/analytics/transaction_history error:", e)
    return null
  }
}

// ========================================
// ========== EVALUATIONS =================
// ========================================

export async function getUserEvaluations() {
  try {
    let response = await api.init(TOKEN).get("/evaluations")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/evaluations error:", e)
    return null
  }
}

export async function getUserEvaluationsCompleted() {
  try {
    let response = await api.init(TOKEN).get("/evaluations/completed")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/evaluations/completed error:", e)
    return null
  }
}

// ========================================
// ========== COMMUNICATION ===============
// ========================================

export async function searchNonCommUsers(id: any, keywords?: any) {
  try {
    let response = await api
      .init(TOKEN)
      .post(`/communication/${id}/user/search`, keywords, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/communication/${id}/user/search error:`, e)
    return null
  }
}

export async function createCommGroup(data: any) {
  try {
    let response = await api.init(TOKEN).post("/communication/group", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/communication/group error:", e)
    return null
  }
}

export async function editCommGroup(data: any) {
  try {
    let response = await api
      .init(TOKEN)
      .patch(`/communication/group/${data.id}`, data.data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/communication/group/${data.id} error:`, e)
    return null
  }
}

export async function deleteCommGroup(id: any) {
  try {
    let response = await api.init(TOKEN).delete(`/communication/group/${id}`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/communication/group/${id} error:`, e)
    return null
  }
}

export async function pinComm(data: any) {
  try {
    let response = await api
      .init(TOKEN)
      .post(`/communication/${data}/pin`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/communication/${data}/pin error:`, e)
    return null
  }
}

export async function postCommViewed(postId: number | string) {
  try {
    const response = await api
      .init(TOKEN)
      .post(`/communication/${postId}/viewed`)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/communication/${postId}/viewed error:`, e)
    return null
  }
}

// ========================================
// ========== ACCOUNT & HISTORY ===========
// ========================================

export async function getHistory() {
  try {
    let response = await api.init(TOKEN).get("/account/history")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/account/history error:", e)
    return null
  }
}

// ========================================
// ========== CATALOG & ORDERS ============
// ========================================

export async function getOrgCatalogs() {
  try {
    let response = await api.init(TOKEN).get("/catalogs")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/catalogs error:", e)
    return null
  }
}

export async function editOrg(data: any) {
  try {
    let response = await api.init(TOKEN).post("/org/edit", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/edit error:", e)
    return null
  }
}

export async function getOrgOrders(data: any) {
  try {
    let response = await api.init(TOKEN).post("/org/orders", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/orders error:", e)
    return null
  }
}

export async function transferAdminPoints(data: any) {
  // Transformar los datos al formato esperado por el backend
  let transformedData: Record<string, any> = {}

  // Si data es un array, procesar múltiples usuarios
  if (Array.isArray(data)) {
    data.forEach((userData, index) => {
      transformedData[(index + 1).toString()] = userData
    })
  } else {
    // Si data es un objeto, procesar un solo usuario
    transformedData["1"] = data
  }

  console.log("transformedData", transformedData)
  try {
    let response = await api
      .init(TOKEN)
      .post("/transfer/admin/points", transformedData)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/transfer/admin/points error:", e)
    return null
  }
}

export async function getAdminPrograms() {
  try {
    let response = await api.init(TOKEN).get(`/admin/program`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/admin/programs error:", e)
    return null
  }
}

// ========================================
// ========== COMPLAINTS & SUGGESTIONS ====
// ========================================

export async function getComplaints() {
  try {
    let response = await api.init(TOKEN).get("/complaints")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/complaints error:", e)
    return null
  }
}

export async function getSuggestions() {
  try {
    let response = await api.init(TOKEN).get("/suggestions")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/suggestions error:", e)
    return null
  }
}

export async function addComplaint(data: any) {
  try {
    let response = await api.init(TOKEN).post("/complaint/add", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/complaint/add error:", e)
    return null
  }
}

export async function archiveComplaint(id: any) {
  try {
    let response = await api.init(TOKEN).patch(`/complaint/${id}/archive`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/complaint/${id}/archive error:`, e)
    return null
  }
}

export async function archiveSuggestion(id: any) {
  try {
    let response = await api.init(TOKEN).patch(`/suggestion/${id}/archive`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/suggestion/${id}/archive error:`, e)
    return null
  }
}

// ========================================
// ========== GOALS =======================
// ========================================

export async function getGoals() {
  try {
    let response = await api.init(TOKEN).get("/goal")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/goal error:", e)
    return null
  }
}

export async function getGoalAvailableWeight(ownerId: string | number) {
  try {
    let response = await api
      .init(TOKEN)
      .post("/goals/available_weight", { owner_id: ownerId })
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/goals/available_weight error:", e)
    return null
  }
}

export async function reorderGoals(id: any, data: any) {
  try {
    let response = await api
      .init(TOKEN)
      .post(`/goal/${id}/checklist/reorder`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/goal/${id}/checklist/reorder error:`, e)
    return null
  }
}

export async function addGoalCheckin(data: any) {
  try {
    let response = await api.init(TOKEN).post("/goal/checkin", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/goal/checkin error:", e)
    return null
  }
}

export async function addOrgGoalCheckin(data: any) {
  try {
    let response = await api.init(TOKEN).post("/org/goal/checkin", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/goal/checkin error:", e)
    return null
  }
}

export async function getGoalPeriods() {
  try {
    let response = await api.init(TOKEN).get("/goal_period")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/goal_period error:", e)
    return null
  }
}

export async function getGoalCategories() {
  try {
    let response = await api.init(TOKEN).get("/goal_category")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/goal_category error:", e)
    return null
  }
}

export async function addPersonalGoal(data: any) {
  try {
    let response = await api.init(TOKEN).post("/goal", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/goal error:", e)
    return null
  }
}

export async function editPersonalGoal(id: any, data: any) {
  try {
    let response = await api.init(TOKEN).patch(`/goal/${id}/`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/goal/${id}/ error:`, e)
    return null
  }
}

export async function editCorporateGoal(id: any, data: any) {
  try {
    let response = await api.init(TOKEN).patch(`/org/goal/${id}/`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/org/goal/${id}/ error:`, e)
    return null
  }
}

export async function addOrgGoal(data: any) {
  try {
    let response = await api.init(TOKEN).post("/org/goal", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/goal error:", e)
    return null
  }
}

export async function addGoalComment(data: any) {
  try {
    let response = await api.init(TOKEN).post("/goal/comment", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/goal/comment error:", e)
    return null
  }
}

export async function createGoalChecklistItem(data: any, id: any) {
  try {
    let response = await api.init(TOKEN).post(`/goal/${id}/checklist`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/goal/${id}/checklist error:`, e)
    return null
  }
}

export async function updateGoalChecklist(goalid: any, data: any) {
  try {
    let response = await api
      .init(TOKEN)
      .patch(`/goal/${goalid}/checklist`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/goal/${goalid}/checklist error:`, e)
    return null
  }
}

export async function deleteChecklist(goalid: any, data: any) {
  try {
    let response = await api
      .init(TOKEN)
      .delete(`/goal/${goalid}/checklist`, { data })
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/goal/${goalid}/checklist error:`, e)
    return null
  }
}

// ========================================
// ========== DEPARTMENTS =================
// ========================================

export async function getAllDepartments() {
  try {
    let response = await api.init(TOKEN).get("/departments/all")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/departments/all error:", e)
    return null
  }
}

export async function addNewDepartment(data: any) {
  try {
    let response = await api.init(TOKEN).post("/org/department", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/department error:", e)
    return null
  }
}

export async function patchDepartment(data: any) {
  try {
    let response = await api.init(TOKEN).patch(`/department/${data.id}`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/department/${data.id} error:`, e)
    return null
  }
}

export async function patchOrgDepartment(id: any, data: any) {
  try {
    let response = await api.init(TOKEN).patch(`/org/department/${id}`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/department/${data.id} error:`, e)
    return null
  }
}

export async function activateDepartment(data: any) {
  try {
    let response = await api
      .init(TOKEN)
      .patch(`/department/${data.id}/activate`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }

  } catch (e) {
    console.error(`/department/${data.id}/activate error:`, e)
    return null
  }
}

// ========================================
// ========== CATEGORIES ==================
// ========================================

export async function addNewGoalCategory(data: any) {
  try {
    let response = await api.init(TOKEN).post("/goal_category", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/goal_category error:", e)
    return null
  }
}

export async function deleteGoalCategory(data: any) {
  try {
    let response = await api.init(TOKEN).delete(`/goal_category/${data.id}`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/goal_category/${data.id} error:`, e)
    return null
  }
}

export async function editGoalCategory(id: any, data: any) {
  try {
    let response = await api.init(TOKEN).patch(`/goal_category/${id}`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/goal_category/${id} error:`, e)
    return null
  }
}

export async function addNewProgramCategory(data: any) {
  try {
    let response = await api.init(TOKEN).post("/program_category", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/program_category error:", e)
    return null
  }
}

export async function deleteProgramCategory(id: any) {
  try {
    let response = await api.init(TOKEN).delete(`/program_category/${id}`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/program_category/${id} error:`, e)
    return null
  }
}

export async function editProgramCategory(id: any, data: any) {
  try {
    let response = await api.init(TOKEN).patch(`/program_category/${id}`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/program_category/${id} error:`, e)
    return null
  }
}

export async function patchPostType(payload: { id: number; data: Record<string, unknown> }) {
  try {
    const { id, data } = payload
    const body = { ...data }
    if (Array.isArray(body.recognition_subtypes)) {
      body.recognition_subtypes = body.recognition_subtypes.map((st: { name?: string }) => ({
        name: st.name,
      }))
    }
    let response = await api.init(TOKEN).patch(`/org/recognition_type/${id}`, body)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/org/recognition_type/${payload.id} error:`, e)
    return null
  }
}

// ========================================
// ========== VALUES ======================
// ========================================

export async function getAllValues() {
  try {
    let response = await api.init(TOKEN).get("/org/values")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/values error:", e)
    return null
  }
}

export async function addNewValue(data: any) {
  try {
    const payload = data.data ?? data
    const body = { name: payload.name, description: payload.description }
    let response = await api.init(TOKEN).post("/org/value", body)
    if (response.status === 200) {
      return response.data?.data ?? response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/value error:", e)
    return null
  }
}

export async function editValue(data: any) {
  try {
    const payload = data.data ?? data
    const body = { name: payload.name, description: payload.description }
    let response = await api.init(TOKEN).patch(`/org/value/${data.id}`, body)
    if (response.status === 200) {
      return response.data?.data ?? response.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/org/value/${data.id} error:`, e)
    return null
  }
}

export async function deleteValue(data: any) {
  try {
    let response = await api.init(TOKEN).delete(`/org/value/${data.id}`)
    if (response.status === 200) {
      return response.data?.data ?? response.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/org/value/${data.id} error:`, e)
    return null
  }
}

// ========================================
// ========== UPLOAD & FILES ==============
// ========================================

export async function getUploadPresign(type: any, data: any) {
  const uploadTypes = [
    "comm-attachment",
    "user-profile-picture",
    "task-attachment",
    "program-attachment",
    "suggestion-attachment",
    "complaint-attachment",
    "goalcheckin-attachment",
    "corp-benef-image",
    "post-attachments",
  ]

  if (!uploadTypes.includes(type)) {
    throw new Error(`Invalid file type ${type}`)
  }

  try {
    let response = await api.init(TOKEN).post(`/upload_presign/${type}`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/upload_presign/${type} error:`, e)
    return null
  }
}

export async function getReadPresign(type: any, data: any) {
  try {
    let response = await api.init(TOKEN).post(`/read_presign/${type}`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/read_presign/${type} error:`, e)
    return null
  }
}

// ========================================
// ========== INVOICES ====================
// ========================================

export async function getInvoices() {
  try {
    let response = await api.init(TOKEN).get("/invoices")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/invoices error:", e)
    return null
  }
}

// ========================================
// ========== ORDERS & STATUS =============
// ========================================

export async function setOrderItemStatus(data: any, id: number) {
  try {
    let response = await api.init(TOKEN).patch(`/order_item/${id}/status`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/order_item/${id}/status error:`, e)
    return null
  }
}

// ========================================
// ========== WHATSAPP ====================
// ========================================

export async function activateWhatsapp(phone: any) {
  try {
    let response = await api.init(TOKEN).post("/whatsapp/activate", phone)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/whatsapp/activate error:", e)
    return null
  }
}

// ========================================
// ========== LANGUAGE ====================
// ========================================

export async function getLanguage() {
  try {
    let response = await api.init(TOKEN).get("/language")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/language error:", e)
    return null
  }
}

// ========================================
// ========== PROFILE IMAGES ==============
// ========================================

export async function updateProfileImage(data: any) {
  try {
    let response = await api.init(TOKEN).post("/user/profile-image", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/user/profile-image error:", e)
    return null
  }
}

export async function updateBackgroundImage(data: any) {
  try {
    let response = await api.init(TOKEN).post("/user/background-image", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/user/background-image error:", e)
    return null
  }
}

// ========================================
// ========== API KEYS ====================
// ========================================

export async function getApiKey() {
  try {
    let response = await api.init(TOKEN).get("/api/key")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/api/key error:", e)
    return null
  }
}

export async function createApiKey(data: any) {
  try {
    let response = await api.init(TOKEN).post("/api/key", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/api/key error:", e)
    return null
  }
}

export async function deleteApiKey(key_id: any) {
  try {
    let response = await api.init(TOKEN).delete(`/api/key/${key_id}`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/api/key/${key_id} error:`, e)
    return null
  }
}

// ========================================
// ========== OUTPUT EVENTS ===============
// ========================================

export async function postOutputEvents(data: any) {
  try {
    let response = await api.init(TOKEN).post("/points/outputs", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/points/outputs error:", e)
    throw e
  }
}

// ========================================
// ========== SURVEYS & ORG ===============
// ========================================

export async function getOrgSurveys() {
  try {
    let response = await api.init(TOKEN).get("/org/surveys")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/surveys error:", e)
    return null
  }
}

export async function getOrgSurveysTypePrograms() {
  try {
    let response = await api.init(TOKEN).get("/org/surveys/programs")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/surveys/programs error:", e)
    return null
  }
}

export async function getOrgSurveysTypeSolicitudes() {
  try {
    let response = await api.init(TOKEN).get("/org/surveys/solicitudes")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/surveys/solicitudes error:", e)
    return null
  }
}

// ========================================
// ========== COUNTRIES ===================
// ========================================

export async function getCountries() {
  try {
    let response = await api.init(TOKEN).get("/countries")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/countries error:", e)
    return null
  }
}

// ========================================
// ========== TYPEFORM ====================
// ========================================

export async function getTypeform(formId: any) {
  try {
    let response = await api.init(TOKEN).get(`/typeform/${formId}`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/typeform/${formId} error:`, e)
    return null
  }
}

export async function getAllTypeforms(page: any) {
  try {
    let response = await api.init(TOKEN).get(`/typeforms?page=${page}`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/typeforms error:", e)
    return null
  }
}

export async function updateTypeform(formId: any, dataToUpdate: any) {
  try {
    let response = await api
      .init(TOKEN)
      .patch(`/typeform/${formId}`, dataToUpdate)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/typeform/${formId} error:`, e)
    return null
  }
}

export async function getTypeformResponse(formId: any) {
  try {
    let response = await api.init(TOKEN).get(`/typeform/${formId}/response`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/typeform/${formId}/response error:`, e)
    return null
  }
}

// ========================================
// ========== PRODUCTS ====================
// ========================================

export async function addProduct(data: any) {
  try {
    let response = await api.init(TOKEN).post("/product", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/product error:", e)
    return null
  }
}

// ========================================
// ========== FORMS =======================
// ========================================

export async function getForms() {
  try {
    let response = await api.init(TOKEN).get("/forms")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/forms error:", e)
    return null
  }
}

export async function getFormById(params: { form_id: any }) {
  try {
    let response = await api.init(TOKEN).get(`/forms/${params.form_id}`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/forms/${params.form_id} error:`, e)
    return null
  }
}

// ========================================
// ========== FITNESS =====================
// ========================================

export async function getTypesFit() {
  try {
    let response = await api.init(TOKEN).get("/fitness/types")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/fitness/types error:", e)
    return null
  }
}

export async function createCategoryFit(data: any) {
  try {
    let response = await api.init(TOKEN).post("/fitness/category", data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/fitness/category error:", e)
    return null
  }
}

// ========================================
// ========== CHALLENGES ==================
// ========================================

export async function getChallenges() {
  try {
    let response = await api.init(TOKEN).get("/challenges")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/challenges error:", e)
    return null
  }
}

// ========================================
// ========== CUSTOM PAGES ===============
// ========================================

export async function getCustomPages() {
  try {
    let response = await api.init(TOKEN).get("/custom/pages")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/custom/pages error:", e)
    return null
  }
}

export async function getCustomPageById(params: { page_id: number }) {
  try {
    let response = await api.init(TOKEN).get(`/custom/page/${params.page_id}`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/custom/page/${params.page_id} error:`, e)
    return null
  }
}

export async function createCustomPage(params: any) {
  try {
    let response = await api.init(TOKEN).post("/custom/page", params)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/custom/page error:", e)
    return null
  }
}

export async function updateCustomPage(params: any) {
  try {
    let response = await api.init(TOKEN).patch("/custom/page", params)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/custom/page error:", e)
    return null
  }
}

export async function deleteCustomPage(params: { page_id: number }) {
  try {
    let response = await api
      .init(TOKEN)
      .delete("/custom/page", { data: params })
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/custom/page error:", e)
    return null
  }
}

// ========================================
// ========== FORMS ANSWERS ===============
// ========================================

export async function sendAnswers(params: { answers: any[] }) {
  try {
    let response = await api.init(TOKEN).post("/forms/answers", params.answers)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/forms/answers error:", e)
    return null
  }
}

// ========================================
// ========== COUPONS & REDEEMS ==========
// ========================================

export async function getUserCoupon(params: {
  user_id: number
  coupon_id: number
}) {
  try {
    let response = await api.init(TOKEN).post("/redeem/user/create", params)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/redeem/user/create error:", e)
    return null
  }
}

export async function getProveedorCupons(provider_id: any) {
  try {
    let response = await api.init(TOKEN).get(`/provider/${provider_id}/coupons`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/provider/${provider_id}/coupons error:`, e)
    return null
  }
}

export async function getRedeemByProvider(params: any) {
  try {
    let response = await api
      .init(TOKEN)
      .get(`/redeem/provider/${params.provider_id}`, {
        params: {
          page: params.page,
          per_page: params.per_page,
        },
      })
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/redeem/provider/${params.provider_id} error:`, e)
    return null
  }
}

export async function redeemCode(key: string) {
  try {
    let response = await api.init(TOKEN).post("/redeem/code", { key })
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/redeem/code error:", e)
    return null
  }
}

export async function getProviders() {
  try {
    let response = await api.init(TOKEN).get("/providers")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/providers error:", e)
    return null
  }
}

export async function getCoupons() {
  try {
    let response = await api.init(TOKEN).get("/coupons")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/coupons error:", e)
    return null
  }
}

export async function getRedeems() {
  try {
    let response = await api.init(TOKEN).get("/redeem")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/redeem error:", e)
    return null
  }
}

export async function getRedeemByProviderCoupons(params: any) {
  try {
    let response = await api
      .init(TOKEN)
      .get(`/coupons/search/provider/${params}`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/coupons/search/provider error:", e)
    return null
  }
}

export async function postRedeemCode(key: string) {
  try {
    let response = await api.init(TOKEN).post("/redeem", { key })
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/redeem error:", e)
    return null
  }
}

export async function getProveedorCupon(provider_id: any) {
  try {
    let response = await api
      .init(TOKEN)
      .get(`/coupons/search/user/${provider_id}`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/redeem error:", e)
    return null
  }
}

// ========================================
// ========== PODIUM ======================
// ========================================

export async function getPodium(accum: boolean) {
  try {
    let response = await api.init(TOKEN).get(`/test/podium?accum=${accum}`)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/podium error:", e)
    return null
  }
}

export async function getPodiumIsg() {
  try {
    let response = await api.init(TOKEN).get("/podium/insignia")
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/podium/insignia error:", e)
    return null
  }
}

// ========================================
// ========== USER EMAILS ================
// ========================================

export async function addUserAdminEmail(params: any) {
  try {
    let response = await api.init(TOKEN).post("/user/admin/email", params)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/user/admin/email error:", e)
    return null
  }
}

export async function addUserEmail(params: any) {
  try {
    let response = await api.init(TOKEN).post("/org/add/email", params)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/add/email error:", e)
    return null
  }
}

export async function addOrgUserAdminEmail(params: any) {
  try {
    let response = await api.init(TOKEN).post("/org/admin/add/email", params)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/user/admin/email error:", e)
    return null
  }
}

export async function updateUserMainEmail(params: any) {
  try {
    let response = await api.init(TOKEN).patch("/org/update/email", params)
    if (response.status === 200) {
      return { data: response.data.data, next: response.data.next }
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/custom_fitness error:", e)
    return null
  }
}

export async function deleteUserAdminMainEmail(params: any) {
  try {
    let response = await api
      .init(TOKEN)
      .delete("/user/admin/email", { data: params })
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/user/admin/email error:", e)
    return null
  }
}

export async function deleteOrgUserAdminMainEmail(params: any) {
  try {
    let response = await api
      .init(TOKEN)
      .delete("/org/admin/delete/email", { data: params })
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/user/admin/email error:", e)
    return null
  }
}

export async function deleteUserMainEmail(params: any) {
  try {
    let response = await api
      .init(TOKEN)
      .delete("/org/delete/email", { data: params })
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/user/admin/email error:", e)
    return null
  }
}

// ========================================
// ========== LDAP =======================
// ========================================

export async function syncLdapUsers() {
  try {
    let response = await api.init(TOKEN).post("/org/ldap/users")
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/ldap/users error:", e)
    return null
  }
}

export async function getLdapUsers() {
  try {
    let response = await api.init(TOKEN).get("/org/ldap/users")
    if (response.status === 200) {
      return response.data?.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/ldap/users error:", e)
    return null
  }
}

// ========================================
// ========== ORGANIZATION STYLES ========
// ========================================

export async function customOrgStylesFit(params: any) {
  try {
    let response = await api.init(TOKEN).patch("/org/custom_fitness", params)
    if (response.status === 200) {
      return { data: response.data.data, next: response.data.next }
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/custom_fitness error:", e)
    return null
  }
}

/////

export async function getUserEmail() {
  try {
    let response = await api.init(TOKEN).get("/emails")
    if (response.status === 200) {
      return response.data?.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/emails error:", e)
    return null
  }
}

/// typeform

export async function getTypeformThemes(page: number) {
  try {
    let response = await api.init(TOKEN).get(`/typeform/themes?page=${page}`)
    if (response.status === 200) {
      return response.data?.data
    } else {
      return null
    }
  } catch (e) {
    console.error("`/typeform/themes` error:", e)
    return null
  }
}

///ORDERS

export async function getOrderDetails(params: GetOrderDetailParams) {
  try {
    let response = await api.init(TOKEN).get(`/orders/${params.order_id}`)
    if (response.status === 200) {
      return response.data?.data
    } else {
      return null
    }
  } catch (e) {
    console.error("`/typeform/themes` error:", e)
    return null
  }
}

////

export async function generebyPrompt(params: PromptMessage) {
  try {
    let response = await api.init(TOKEN).post(`/org/ia_generator`, params)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/ia_generator error:", e)
    return null
  }
}

////

export async function addCorporateBenefit(params: any) {
  try {
    let response = await api
      .init(TOKEN)
      .post(`/corporate_benefits/create`, params)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/corporate_benefits/create error:", e)
    return null
  }
}

export async function getCorpBenef() {
  try {
    let response = await api.init(TOKEN).get(`/corporate_benefits`)
    if (response.status === 200) {
      return response.data?.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/corporate_benefits error:", e)
    return null
  }
}

export async function addSubmissionCorpBenef(params: any) {
  try {
    let response = await api
      .init(TOKEN)
      .post(`/corporate_benefits/submission`, params)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/corporate_benefits/create error:", e)
    return null
  }
}

export async function updateCorpBenef(updatedData: any) {
  try {
    let response = await api
      .init(TOKEN)
      .patch(`/corporate_benefits/update/${updatedData.benefitId}`, updatedData)
    if (response.status === 200) {
      return { data: response.data.data, next: response.data.next }
    } else {
      return null
    }
  } catch (e) {
    console.error("/corporate_benefits/update error:", e)
    return null
  }
}

export async function archiveCorpBenefit(updatedData: any) {
  try {
    let response = await api
      .init(TOKEN)
      .patch(
        `/corporate_benefits/archive/${updatedData.benefitId}`,
        updatedData
      )
    if (response.status === 200) {
      return { data: response.data.data, next: response.data.next }
    } else {
      return null
    }
  } catch (e) {
    console.error("/corporate_benefits/archive error:", e)
    return null
  }
}

////

export async function updateUserActive() {
  try {
    let response = await api.init(TOKEN).post(`/update_user_active`)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/update_user_active error:", e)
    return null
  }
}

////

export async function getSubmissionCorp() {
  try {
    let response = await api.init(TOKEN).get("/corporate_benefits/submission")
    if (response.status === 200) {
      return response.data?.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/corporate_benefits/submission error:", e)
    return null
  }
}

//// LEVELS

export async function postLevels(params: any) {
  try {
    let response = await api.init(TOKEN).post(`/levels/create`, params)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/levels/create error:", e)
    return null
  }
}

export async function getLevels() {
  try {
    let response = await api.init(TOKEN).get("/levels")
    if (response.status === 200) {
      return response.data?.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/levels error:", e)
    return null
  }
}

////

export async function updateSubmissionUserActive(params: {
  user_id: any
  benefit_id: any
  user_active: boolean
}) {
  try {
    const path = `/update_user_active/${params?.user_id}/${params?.benefit_id}`
    const requestData = { user_active: params?.user_active } // Prepare the request data

    let response = await api.init(TOKEN).patch(path, requestData)
    if (response.status === 200) {
      return { data: response.data.data, next: response.data.next }
    } else {
      return null
    }
  } catch (e) {
    console.error("/update_user_active error:", e)
    return null
  }
}

////

export async function postSolicitud(params: any) {
  try {
    let response = await api.init(TOKEN).post(`/solicitud/create`, params)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/solicitud/create error:", e)
    return null
  }
}

export async function getSolicitudes() {
  try {
    let response = await api.init(TOKEN).get("/solicitud")
    if (response.status === 200) {
      return response.data?.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/solicitud error:", e)
    return null
  }
}

export async function updateSolicitud(params: any) {
  try {
    let response = await api
      .init(TOKEN)
      .patch(`/solicitud/update/${params.solicitudId}`, params)
    if (response.status === 200) {
      return { data: response.data.data, next: response.data.next }
    } else {
      return null
    }
  } catch (e) {
    console.error("/solicitud/update error:", e)
    return null
  }
}

////

export async function submissionSolicitud(id: any, params: any) {
  try {
    let response = await api.init(TOKEN).post(`/solicitud/${id}/submit`, params)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/solicitud/create error:", e)
    return null
  }
}

export async function getAllSurveysUser() {
  try {
    let response = await api.init(TOKEN).get("/all_surveys_users")
    if (response.status === 200) {
      return response.data?.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/all_surveys_users error:", e)
    return null
  }
}

////

export async function updateSurveyUser(params: any) {
  try {
    let response = await api
      .init(TOKEN)
      .patch(`/survey_user/${params.id}`, params)
    if (response.status === 200) {
      return { data: response.data.data, next: response.data.next }
    } else {
      return null
    }
  } catch (e) {
    console.error("/survey_user error:", e)
    return null
  }
}

////WHATSAPP

export async function deactivateWhatsapp() {
  try {
    let response = await api.init(TOKEN).post(`/user/whatsapp/deactivate`)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/user/whatsapp/deactivate error:", e)
    return null
  }
}

////

export async function getAllProductsCat(page: any, category: any) {
  try {
    let path = "/products"
    path += category !== -1 ? `/category/${category}` : ""
    let response = await api.init(TOKEN).post(path, page)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/user/whatsapp/deactivate error:", e)
    return null
  }
}

//// TASK

export async function deleteTask(taskId: any) {
  try {
    let response = await api.init(TOKEN).delete(`/task/${taskId}/`)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.log(`/task/${taskId}/`)
    console.log(e)
    return null
  }
}

export async function getTasksCreated(page: any) {
  try {
    let response = await api.init(TOKEN).get("/user/tasks/created", page)
    if (response.status === 200) {
      return response.data?.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/user/tasks/created error:", e)
    return null
  }
}

export async function getTasksSubmitted(page: any) {
  try {
    let response = await api.init(TOKEN).get("/user/tasks/submitted", page)
    if (response.status === 200) {
      return response.data?.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/user/tasks/submitted error:", e)
    return null
  }
}

////

export async function getOrgUsers(
  type?: any,
  page?: any,
  keywords?: string,
  canSearchByEmail?: boolean,
  active?: string
) {
  try {
    const path = "/org/users"
    const params: any = {
      page: page || 1,
      type,
      active: active || "active",
      keywords,
      search_by_email: canSearchByEmail ? "true" : "false",
    }

    // Limpiar valores undefined
    Object.keys(params).forEach((key) => {
      if (
        params[key] === undefined ||
        params[key] === null ||
        params[key] === ""
      ) {
        delete params[key]
      }
    })

    const response = await api.init(TOKEN).get(path, { params })

    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/users error:", e)
    return null
  }
}

////

export async function calculatePointsToDistribute(data: any) {
  try {
    let response = await api.init(TOKEN).post("/org/points_to_distribute", data)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/points_to_distribute error:", e)
    return null
  }
}

export async function changeUserExtraPoints(data: any) {
  try {
    let response = await api.init(TOKEN).post("/user/extra_points", data)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/user/extra_points error:", e)
    return null
  }
}

export async function changeBasePoints(data: any) {
  try {
    let response = await api.init(TOKEN).post("/org/base_points", data)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/base_points error:", e)
    return null
  }
}

////

export async function approveRquest(data: any) {
  try {
    let response = await api.init(TOKEN).post("/point_request/approve", data)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/point_request/approve error:", e)
    return null
  }
}

export async function rejectRequest(data: any) {
  try {
    let response = await api.init(TOKEN).post("/point_request/reject", data)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/point_request/reject error:", e)
    return null
  }
}

////

export async function getPayments() {
  try {
    let response = await api.init(TOKEN).get("/payments")
    if (response.status === 200) {
      return response.data?.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/payments error:", e)
    return null
  }
}
////
export async function sendOrgRecognition(id: any, data: any) {
  try {
    let response = await api
      .init(TOKEN)
      .post(`/org/send/recognition/${id}`, data)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/send/recognition error:", e)
    return null
  }
}

export async function getSpecialRecognitionsHistory(id: any) {
  try {
    const path = "/org/get_recognition_by_id/"
    const params = {
      id_recognition: id,
    }

    const response = await api.init(TOKEN).get(path, { params })

    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/org/get_recognition_by_id/ error:", e)
    return null
  }
}

////TYPEFORMS

export const createNewTypeform = async (formData: any, rawHeaders: any) => {
  const path = "/typeform/create"

  const config = rawHeaders.headers ? rawHeaders : { headers: rawHeaders }

  const response = await api.init(TOKEN).post(path, formData, config)
  if (response.status === 200) {
    return response.data
  } else {
    return null
  }
}

////

export async function getQuestionsForm() {
  try {
    let response = await api.init(TOKEN).get("/forms/questions")
    if (response.status === 200) {
      return response.data?.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/forms/questions error:", e)
    return null
  }
}

// * * * * * * * * * * * * * *
// * * * RECOGNITION GROUP * *
// * * * * * * * * * * * * * *

export async function postGroupRecognition(data: any) {
  try {
    let response = await api.init(TOKEN).post(`/recognition/group`, data)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/recognition/group error:", e)
    return null
  }
}

export async function getGroupRecognition() {
  try {
    let response = await api.init(TOKEN).get("/recognition/groups")
    if (response.status === 200) {
      return response.data?.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/recognition/groups error:", e)
    return null
  }
}

export async function deleteRecognitionGroup(id: any) {
  try {
    let response = await api.init(TOKEN).delete(`/recognition/group/${id}`)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/program_category/${id} error:`, e)
    return null
  }
}

export async function editRecognitionGroup(data: any) {
  try {
    let response = await api
      .init(TOKEN)
      .patch(`/recognition/group/${data.id}`, data)
    if (response.status === 200) {
      return response.data.data
    } else {
      return null
    }
  } catch (e) {
    console.error(`/recognition/group/${data.id} error:`, e)
    return null
  }
}

// * * * * * * * * * * * * * * *
// * * * RECOGNITION PRESETS * *
// * * * * * * * * * * * * * * *

export async function sendPresetRecognitionRequest(data: {
  receiver_ids: number[]
  preset_recognition_id: number
  message?: string
  user_response_1: string
  user_response_2: string
}) {
  try {
    let response = await api
      .init(TOKEN)
      .post(`/preset-recognition/request`, data)

    if (response.status === 200 || response.status === 201) {
      return response.data
    } else {
      // Lanzar error para que React Query lo capture
      throw new Error(
        response.data?.error ||
          `Error ${response.status}: ${response.statusText}`
      )
    }
  } catch (e) {
    console.error("/preset-recognition/request error:", e)
    // Re-lanzar el error para que React Query lo capture
    throw e
  }
}

export async function getAssignedPresetRecognitionRequests(
  statuses: string[] = []
) {
  try {
    const query = statuses
      .map((s) => `status=${encodeURIComponent(s.toUpperCase())}`)
      .join("&")

    let response = await api
      .init(TOKEN)
      .get(`/preset-recognition/assigned-requests?${query}`)

    if (response.status === 200) {
      return response.data?.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/preset-recognition/assigned-requests error:", e)
    return null
  }
}

export async function getPresetRecognitionNewsfeed() {
  try {
    let response = await api.init(TOKEN).get("/preset-recognitions")
    if (response.status === 200) {
      return response.data?.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/preset-recognitions error:", e)
    return null
  }
}

export async function getPresetRecognitionTypesNewsfeed() {
  try {
    let response = await api.init(TOKEN).get("/preset-recognitions/types")
    if (response.status === 200) {
      return response.data?.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/preset-recognitions error:", e)
    return null
  }
}

export async function getPresetRecognitions(statuses?: any) {
  try {
    const query = statuses
      .map((s: any) => `status=${encodeURIComponent(s.toUpperCase())}`)
      .join("&")

    let response = await api
      .init(TOKEN)
      .get(`/preset-recognition/requests?${query}`)

    if (response.status === 200) {
      return response.data?.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/preset-recognition/requests error:", e)
    return null
  }
}

// Obtener límites de aprobación de reconocimientos
export async function getPresetRecognitionApprovalLimits() {
  try {
    let response = await api
      .init(TOKEN)
      .get("/preset-recognition/approval-limits")

    if (response.status === 200) {
      return response.data?.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/preset-recognition/approval-limits error:", e)
    return null
  }
}

// Actualizar límites de aprobación de reconocimientos
export async function updatePresetRecognitionApprovalLimits(data: {
  preset_recognition_type_id: number
  approval_limit_per_user?: number | null
  approval_limit_per_month?: number | null
}) {
  try {
    let response = await api
      .init(TOKEN)
      .patch("/admin/preset-recognition-type/approval-limits", data)

    if (response.status === 200) {
      return response.data?.data
    } else {
      throw new Error(
        response.data?.error ||
          `Error ${response.status}: ${response.statusText}`
      )
    }
  } catch (e) {
    console.error("/admin/preset-recognition-type/approval-limits error:", e)
    throw e
  }
}

// Obtener preset recognitions recibidas (para Coloplast)
export async function getReceivedPresetRecognitions() {
  try {
    let response = await api
      .init(TOKEN)
      .get("/preset-recognitions/received-recognitions")

    if (response.status === 200) {
      return response.data?.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/preset-recognitions/received-recognitions error:", e)
    return null
  }
}

//// APROBAR O RECHAZAR RECO

export async function postApprovedRejectedReco(data: any) {
  try {
    let response = await api
      .init(TOKEN)
      .post(`/preset-recognition/request/fulfill`, data)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/preset-recognition/request/fulfill error:", e)
    return null
  }
}

//// CREATE PRESET

export async function postPresetRecoAdmin(data: any) {
  try {
    let response = await api.init(TOKEN).post(`/admin/preset-recognition`, data)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/admin/preset-recognition error:", e)
    return null
  }
}

export async function editPresetRecoAdmin(data: any) {
  try {
    let response = await api
      .init(TOKEN)
      .patch(`/admin/preset-recognition`, data)
    if (response.status === 200) {
      return response.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/admin/preset-recognition error:", e)
    return null
  }
}

export async function getPresetRecognitionAdmin() {
  try {
    let response = await api.init(TOKEN).get("/admin/preset-recognitions")
    if (response.status === 200) {
      return response.data?.data
    } else {
      return null
    }
  } catch (e) {
    console.error("/admin/preset-recognitions error:", e)
    return null
  }
}

export async function getCountRecoUserMonth() {
  try {
    let response = await api
      .init(TOKEN)
      .get("/preset-recognition/requests/count")
    if (response.status === 200) {
      return response.data?.data
    } else {
      return null
    }
  } catch (e: any) {
    // Silenciar error 403 (sin permisos) ya que es un caso esperado para algunos usuarios
    if (e?.response?.status === 403) {
      return null
    }
    console.error("/preset-recognition/requests/count error:", e)
    return null
  }
}

// COLOPLAST USERS MANAGEMENT ENDPOINTS

// * * * GET ROLES * * *
// * * * * * * * * * * * * * * *
export async function getRoles() {
  try {
    console.log("🌐 API call - getRoles:", "/org/roles")
    let response = await api.init(TOKEN).get("/org/roles")
    console.log("✅ API response - getRoles:", response)
    return response.data?.data
  } catch (error) {
    console.error("❌ API error - getRoles:", error)
    return null
  }
}

// * * * UPDATE ROLE * * *
// * * * * * * * * * * * * * * *
export async function updateRole(roleData: unknown) {
  try {
    console.log("🌐 API call - updateRole:", "/user/role")
    let response = await api.init(TOKEN).patch("/user/role", roleData)
    console.log("✅ API response - updateRole:", response)
    return response.data
  } catch (error) {
    console.error("❌ API error - updateRole:", error)
    return null
  }
}

// * * * CREATE ROLE * * *
// * * * * * * * * * * * * * * *
export async function createRole(roleData: unknown) {
  try {
    console.log("🌐 API call - createRole:", "/user/role")
    let response = await api.init(TOKEN).post("/user/role", roleData)
    console.log("✅ API response - createRole:", response)
    return response.data
  } catch (error) {
    console.error("❌ API error - createRole:", error)
    return null
  }
}

// * * * DELETE ROLE * * *
// * * * * * * * * * * * * * * *
export async function deleteRole(roleData: unknown) {
  try {
    console.log("🌐 API call - deleteRole:", "/user/role")
    let response = await api.init(TOKEN).delete("/user/role", {
      data: roleData,
    })
    console.log("✅ API response - deleteRole:", response)
    return response.data
  } catch (error) {
    console.error("❌ API error - deleteRole:", error)
    return null
  }
}

// * * * ADMIN CREATE USER * * *
// * * * * * * * * * * * * * * *
export async function adminCreateOrgUser(userData: unknown) {
  try {
    console.log("🌐 API call - createUser:", "/org/admin/create/user")
    let response = await api
      .init(TOKEN)
      .post("/org/admin/create/user", userData)
    console.log("✅ API response - createUser:", response)
    return response.data
  } catch (error) {
    console.error("❌ API error - createUser:", error)
    return null
  }
}

// * * * ADMIN UPDATE USER * * *
// * * * * * * * * * * * * * * *
export async function adminUpdateOrgUser(userData: unknown) {
  try {
    console.log("🌐 API call - updateUser:", "/org/admin/update/user")
    let response = await api
      .init(TOKEN)
      .patch("/org/admin/update/user", userData)
    console.log("✅ API response - updateUser:", response)
    return response.data
  } catch (error) {
    console.error("❌ API error - updateUser:", error)
    return null
  }
}

// * * * ADMIN GET EXCEL USERS TEMPLATE * * *
// * * * * * * * * * * * * * * *
export async function getOrgUploadUsersTemplate() {
  try {
    console.log(
      "🌐 API call - getOrgUploadUsersTemplate:",
      "/org/users/create/schedule-template"
    )
    let response = await api
      .init(TOKEN)
      .get("/org/users/create/schedule-template", { responseType: "blob" })
    console.log("✅ API response - getOrgUploadUsersTemplate:", response)
    return response.data
  } catch (error) {
    console.error("❌ API error - getOrgUploadUsersTemplate:", error)
    return null
  }
}

// * * * ADMIN CREATE USERS WITH EXCEL TEMPLATE * * *
// * * * * * * * * * * * * * * *
/** Coloplast: subida síncrona con detalle por fila (200 + results). */
export async function orgAdminUploadUsersBulk(formData: FormData) {
  try {
    console.log(
      "🌐 API call - orgAdminUploadUsersBulk:",
      "/org/admin/upload/users"
    )
    const response = await api
      .init(TOKEN)
      .post("/org/admin/upload/users", formData, {
        headers: {
          "Content-Type": undefined,
        },
      })
    console.log("✅ API response - orgAdminUploadUsersBulk:", response)
    if (response.status === 200 && response.data?.success) {
      return response.data as {
        success: boolean
        summary?: { total: number; successful: number; failed: number }
        results: Array<{
          email: string
          error?: string
          row: number
          success: boolean
        }>
      }
    }
    throw new Error("Error en la respuesta del servidor")
  } catch (error) {
    console.error("❌ API error - orgAdminUploadUsersBulk:", error)
    throw error
  }
}

export async function orgAdminCreateManyUsersExcel(file: Blob) {
  try {
    console.log(
      "🌐 API call - orgAdminCreateManyUsersExcel:",
      "/org/users/create/schedule"
    )
    const formData = new FormData()
    formData.append("sheet", file)
    let response = await api
      .init(TOKEN)
      .post("/org/users/create/schedule", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
    console.log("✅ API response - orgAdminCreateManyUsersExcel:", response)
    if (
      (response.status === 200 || response.status === 201) &&
      response.data?.success
    ) {
      return response.data as { success: boolean; data: string }
    }
    throw new Error("Error en la respuesta del servidor")
  } catch (error) {
    console.error("❌ API error - orgAdminCreateManyUsersExcel:", error)
    return null
  }
}

export async function adminGetRecognitionRequests(page: Number) {
  try {
    console.log(
      "🌐 API call - adminGetRecognitionRequests",
      "/admin/analytics/preset-recognitions/all"
    )
    let response = await api
      .init(TOKEN)
      .get("/admin/analytics/preset-recognitions/all", {
        params: { page },
      })
    console.log("✅ API response - adminGetRecognitionRequests", response)
    return response.data.data
  } catch (error) {
    console.error("❌ API error - adminGetRecognitionRequests", error)
    return null
  }
}

export async function adminGetRecognitionRequestsStatusData({
  type,
  startDate,
  endDate,
}: any) {
  try {
    console.log(
      "🌐 API call - adminGetRecognitionRequestsStatusData",
      "/admin/analytics/preset-recognitions/status-bar-chart"
    )
    let response = await api
      .init(TOKEN)
      .get("/admin/analytics/preset-recognitions/status-bar-chart", {
        params: {
          preset_recognition_type_id: type,
          start_date: startDate,
          end_date: endDate,
        },
      })
    console.log(
      "✅ API response - adminGetRecognitionRequestsStatusData",
      response
    )
    return response.data.data
  } catch (error) {
    console.error("❌ API error - adminGetRecognitionRequestsStatusData", error)
    return null
  }
}

export async function adminGetPresetRecognitionNames() {
  try {
    console.log(
      "🌐 API call - adminGetPresetRecognitionNames",
      "/admin/analytics/preset-recognitions/names"
    )
    let response = await api
      .init(TOKEN)
      .get("/admin/analytics/preset-recognitions/names")
    console.log("✅ API response - adminGetPresetRecognitionNames", response)
    return response.data.data
  } catch (error) {
    console.error("❌ API error - adminGetPresetRecognitionNames", error)
    return null
  }
}

export async function adminGetPresetRecognitionTypesPieChartData({
  type,
  startDate,
  endDate,
}: any) {
  try {
    console.log(
      "🌐 API call - adminGetPresetRecognitionTypesPieChartData",
      "/admin/analytics/preset-recognitions/pie-chart"
    )
    let response = await api
      .init(TOKEN)
      .get("/admin/analytics/preset-recognitions/pie-chart", {
        params: {
          preset_recognition_type_id: type,
          start_date: startDate,
          end_date: endDate,
        },
      })
    console.log(
      "✅ API response - adminGetPresetRecognitionTypesPieChartData",
      response
    )
    return response.data.data
  } catch (error) {
    console.error(
      "❌ API error - adminGetPresetRecognitionTypesPieChartData",
      error
    )
    return null
  }
}

export async function adminGetRecognitionRequestsTimelineData({
  type,
  startDate,
  endDate,
}: any) {
  try {
    console.log(
      "🌐 API call - adminGetRecognitionRequestsTimelineData",
      "/admin/analytics/preset-recognitions/timeline-chart"
    )
    let response = await api
      .init(TOKEN)
      .get("/admin/analytics/preset-recognitions/timeline-chart", {
        params: {
          preset_recognition_type_id: type,
          start_date: startDate,
          end_date: endDate,
        },
      })
    console.log(
      "✅ API response - adminGetRecognitionRequestsTimelineData",
      response
    )
    return response.data.data
  } catch (error) {
    console.error(
      "❌ API error - adminGetRecognitionRequestsTimelineData",
      error
    )
    return null
  }
}

export async function adminGetOrganizationBalanceLogs({
  page = 1,
  per_page = 20,
}: {
  page?: number
  per_page?: number
} = {}) {
  try {
    console.log(
      "🌐 API call - adminGetOrganizationBalanceLogs",
      "/organization/balance-logs"
    )
    let response = await api
      .init(TOKEN)
      .get("/organization/balance-logs", {
        params: {
          page,
          per_page,
        },
      })
    console.log("✅ API response - adminGetOrganizationBalanceLogs", response)
    return response.data
  } catch (error) {
    console.error("❌ API error - adminGetOrganizationBalanceLogs", error)
    return null
  }
}

export async function getEntraIDLoginURL() {
  try {
    console.log(
      "🌐 API call - getEntraIDLoginURL",
      "/org/sso/entra-id-login-url"
    )
    let response = await api.init().get("/org/sso/entra-id-login-url")
    console.log("✅ API response - getEntraIDLoginURL", response)
    return response.data.data
  } catch (error) {
    console.error("❌ API error - getEntraIDLoginURL", error)
    return null
  }
}


// ADMIN PRESET RECOGNITION REQUESTS WITH FILTERS


type AdminReqRecoParams = {
  page?: number;
  perPage?: number;
  statuses?: string[];      // ["PENDING_ADMIN", "APPROVED", "REJECTED"]
  startDate?: string | null;
  endDate?: string | null;
};

export async function getAdminReqReco(params: AdminReqRecoParams = {}) {
  const {
    page = 1,
    perPage = 10,
    statuses,
    startDate,
    endDate,
  } = params;

  try {
    console.log("🌐 API call - /admin/preset-recognition/requests", params);

    // Construir query string manualmente para arrays repetidos
    const queryParts: string[] = [];
    queryParts.push(`page=${page}`);
    queryParts.push(`per_page=${perPage}`);

    // Agregar múltiples parámetros status si existen
    if (statuses && statuses.length > 0) {
      statuses.forEach((status) => {
        queryParts.push(`status=${encodeURIComponent(status)}`);
      });
    }

    if (startDate) {
      queryParts.push(`start_date=${encodeURIComponent(startDate)}`);
    }
    if (endDate) {
      queryParts.push(`end_date=${encodeURIComponent(endDate)}`);
    }

    const queryString = queryParts.join("&");
    const url = `/admin/preset-recognition/requests?${queryString}`;

    const response = await api.init(TOKEN).get(url);


    return response.data?.data; // { requests, total, page, per_page, ... }
  } catch (error) {
    console.error("❌ API error - getAdminReqReco:", error);
    return null;
  }
}


type FinalizeAdminRequestParams = {
  request_id: number;
  status: "APPROVED" | "REJECTED";
  reason?: string | null;
};

export async function finalizeAdminPresetRecognitionRequest(params: FinalizeAdminRequestParams) {
  try {
    console.log("🌐 POST /admin/preset-recognition/request/finalize", params);

    const response = await api
      .init(TOKEN)
      .post("/admin/preset-recognition/request/finalize", params);

    console.log("✅ FINALIZE response:", response);

    return response.data?.data;
  } catch (error: any) {
    console.error("❌ API error - finalizeAdminPresetRecognitionRequest:", error);
    throw error?.response?.data || error;
  }
}

export async function downloadBulkRecognitionTemplate(): Promise<Blob> {
  try {
    const response = await api
      .init(TOKEN)
      .get("/admin/preset-recognition/bulk-template", { responseType: "blob" });
    return response.data;
  } catch (error: any) {
    console.error("❌ API error - downloadBulkRecognitionTemplate:", error);
    throw error?.response?.data || error;
  }
}

export interface BulkUploadResult {
  processed: number;
  errors_count: number;
  successes: Array<{ row: number; receiver: string }>;
  errors: Array<{ row: number; reason: string }>;
}

export async function bulkUploadRecognitions(file: File): Promise<BulkUploadResult> {
  try {
    const formData = new FormData();
    formData.append("sheet", file);

    const response = await api
      .init(TOKEN)
      .post("/admin/preset-recognition/bulk-upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

    return response.data?.data;
  } catch (error: any) {
    console.error("❌ API error - bulkUploadRecognitions:", error);
    throw error?.response?.data || error;
  }
}


